// src/pages/AddConsultaPage.jsx
//El formulario de la consulta médica como tal

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box, Typography, TextField, Button, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Stack, Grid,
  List, ListItem, ListItemText, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Paper, Fade, Avatar, Divider,
  Select, MenuItem, FormControl, InputLabel, InputAdornment,
  FormControlLabel, Switch, Chip, Tooltip, Zoom, Checkbox, FormGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import Autocomplete from '@mui/material/Autocomplete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import debounce from 'lodash/debounce';
import axiosInstance from '../axiosInstance';
import { useAuth } from '../context/AuthContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import HealingIcon from '@mui/icons-material/Healing';
import BiotechIcon from '@mui/icons-material/Biotech';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import MedicationIcon from '@mui/icons-material/Medication';
import PregnantWomanIcon from '@mui/icons-material/PregnantWoman';
import ChildCareIcon from '@mui/icons-material/ChildCare';import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SendIcon from '@mui/icons-material/Send'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import MicIcon from '@mui/icons-material/Mic';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import HistoryIcon from '@mui/icons-material/History';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import EventNoteIcon from '@mui/icons-material/EventNote';
import JoditEditor from 'jodit-react';
import { useClinic } from '../context/ClinicContext';
import { alpha } from '@mui/material/styles';
import {
  softShadow, softShadowHover, cardShadow, radius,
  sectionTone, sectionAccordionSx, sectionSummarySx, sectionDetailsSx,
  sectionIconBoxSx, heroSurfaceSx,
} from './consultaTheme';


function stripHtmlTags(str) {
  if (!str) return '';
  return str.replace(/\<\/?[^>]+(>|$)/g, "");
}

// Plantilla de Antecedentes Gineco-Obstétricos (para pacientes femeninas)
const GINECO_OBSTETRICO_TEMPLATE = `FUM (Fecha Última Menstruación): 
FPP (Regla de Naegele): 
Gestas (G):     Partos (P):     Cesáreas (C):     Abortos (A):     Hijos Vivos (HV):     Hijos Muertos (HM):

Menarquia: 
Menopausia: 
Ciclo:     días —       Regular/Irregular
Dismenorrea:           Sí / No

Método anticonceptivo actual: 
Embarazos previos y desenlaces:
- 

Citología (Papanicolaou):          Fecha:           Resultado: 
Mamografía:                        Fecha:           Resultado: 

Lactancia actual:  Sí / No

Antecedentes obstétricos relevantes:
- 
`;

// Plantilla predefinida para el examen físico con formato mejorado
const EXAMEN_FISICO_TEMPLATE_HTML = `
<p><strong>Neurológico:</strong></p>
<p><strong>Cabeza y Cuello:</strong></p>
<p><strong>ORL:</strong></p>
<p><strong>Cardiopulmonar:</strong></p>
<p><strong>Abdomen:</strong></p>
<p><strong>Genitourinario:</strong></p>
<p><strong>Extremidades:</strong></p>
`;

// Plantilla en blanco
const EXAMEN_FISICO_TEMPLATE_BLANK = '<p></p>';

// Plantilla de Examen Neurológico (HTML)
const EXAMEN_NEUROLOGICO_TEMPLATE_HTML = `
<p><strong>Examen Neurológico</strong></p>

<p><strong>Pares craneales:</strong><br>
- I (Olfatorio):<br>
- II (Óptico):<br>
- III, IV, VI (Motor ocular común, Patético, Abducens):<br>
- V (Trigémino):<br>
- VII (Facial):<br>
- VIII (Vestibulococlear):<br>
- IX, X (Glosofaríngeo, Vago):<br>
- XI (Accesorio espinal):<br>
- XII (Hipogloso):<br>
</p>

<p><strong>Fuerza muscular (Miembros Superiores):</strong></p>
<table style="width:100%; border-collapse:collapse;" border="1" cellpadding="6">
  <tr>
    <th style="text-align:left;">Prueba</th>
    <th style="text-align:center;">D</th>
    <th style="text-align:center;">I</th>
  </tr>
  <tr>
    <td>Abducción / Aducción de hombro</td>
    <td style="text-align:center;"></td>
    <td style="text-align:center;"></td>
  </tr>
  <tr>
    <td>Extensión de brazo</td>
    <td style="text-align:center;"></td>
    <td style="text-align:center;"></td>
  </tr>
  <tr>
    <td>Bíceps</td>
    <td style="text-align:center;"></td>
    <td style="text-align:center;"></td>
  </tr>
  <tr>
    <td>Lumbrical</td>
    <td style="text-align:center;"></td>
    <td style="text-align:center;"></td>
  </tr>
  <tr>
    <td>Interóseos</td>
    <td style="text-align:center;"></td>
    <td style="text-align:center;"></td>
  </tr>
  <tr>
    <td>Extensión de mano</td>
    <td style="text-align:center;"></td>
    <td style="text-align:center;"></td>
  </tr>
</table>

<p><strong>Fuerza muscular (Miembros Inferiores):</strong></p>
<table style="width:100%; border-collapse:collapse;" border="1" cellpadding="6">
  <tr>
    <th style="text-align:left;">Prueba</th>
    <th style="text-align:center;">D</th>
    <th style="text-align:center;">I</th>
  </tr>
  <tr>
    <td>Abducción / Aducción</td>
    <td style="text-align:center;"></td>
    <td style="text-align:center;"></td>
  </tr>
  <tr>
    <td>Flexión de muslo</td>
    <td style="text-align:center;"></td>
    <td style="text-align:center;"></td>
  </tr>
  <tr>
    <td>Extensión de muslo</td>
    <td style="text-align:center;"></td>
    <td style="text-align:center;"></td>
  </tr>
  <tr>
    <td>Flexión / Extensión de pie</td>
    <td style="text-align:center;"></td>
    <td style="text-align:center;"></td>
  </tr>
  <tr>
    <td>Dedo gordo (Hallux)</td>
    <td style="text-align:center;"></td>
    <td style="text-align:center;"></td>
  </tr>
</table>

<p><strong>Reflejos:</strong><br>
- Bicipital: <br>
- Tricipital: <br>
- Estiloradial: <br>
- Rotuliano: <br>
- Aquiliano: <br>
</p>

<p><strong>Signos:</strong><br>
- Homans: <br>
- Trommer: <br>
</p>

<p><strong>Sensibilidad:</strong><br>
- Superficial (tacto, dolor, temperatura): <br>
- Profunda (propiocepción, vibración): <br>
- Alodinia / Hiperestesia: <br>
</p>

<p><strong>Respuesta plantar:</strong> <br></p>

<p><strong>Signos frontales:</strong> <br></p>

<p><strong>Otros hallazgos:</strong> <br></p>
`.trim();

const EXAMEN_PSIQUIATRICO_TEMPLATE_HTML = `
<p><strong>Examen del Estado Mental</strong></p>

<p><strong>Apariencia y actitud:</strong><br>
[ ] Se presenta aseado, con vestimenta acorde y actitud colaboradora.<br>
[ ] Se muestra ligeramente descuidado, aunque accesible al diálogo.<br>
[ ] Actitud poco colaboradora, con cierta resistencia durante la entrevista.<br>
</p>

<p><strong>Conciencia y orientación:</strong><br>
[ ] Se encuentra alerta y orientado en tiempo, espacio y persona.<br>
[ ] Alerta, con leves fallas en la orientación temporal.<br>
[ ] Vigil pero con fluctuaciones en la atención.<br>
</p>

<p><strong>Lenguaje:</strong><br>
[ ] Lenguaje fluido, coherente y bien articulado.<br>
[ ] Lenguaje escaso, con latencias en la respuesta.<br>
[ ] Discurso acelerado, difícil de interrumpir.<br>
</p>

<p><strong>Ánimo y afecto:</strong><br>
[ ] Refiere ánimo bajo; afecto congruente, con reactividad conservada.<br>
[ ] Ánimo ansioso, con afecto tenso pero modulable.<br>
[ ] Ánimo eutímico, con afecto estable.<br>
[ ] Afecto plano, con escasa expresividad emocional.<br>
</p>

<p><strong>Pensamiento (curso y contenido):</strong><br>
[ ] El pensamiento es organizado, sin evidencia de ideas delirantes ni ideación suicida.<br>
[ ] Se observa rumiación persistente, sin contenido delirante.<br>
[ ] Contenido del pensamiento con ideas de minusvalía y culpa.<br>
[ ] Expresa ideación suicida, sin plan estructurado. ⚠️<br>
[ ] Se identifican ideas delirantes de tipo persecutorio. ⚠️<br>
</p>

<p><strong>Percepción:</strong><br>
[ ] No se evidencian alteraciones perceptivas.<br>
[ ] Refiere fenómenos perceptivos auditivos compatibles con alucinaciones. ⚠️<br>
</p>

<p><strong>Cognición:</strong><br>
[ ] Funciones cognitivas globalmente conservadas.<br>
[ ] Atención disminuida durante la entrevista.<br>
[ ] Dificultades leves en memoria reciente.<br>
</p>

<p><strong>Insight y juicio:</strong><br>
[ ] Reconoce parcialmente su estado y la necesidad de tratamiento.<br>
[ ] Escaso insight respecto a su condición.<br>
[ ] Juicio conservado para la vida cotidiana.<br>
[ ] Juicio comprometido en la toma de decisiones.<br>
</p>
`.trim();

// Función para obtener el contenido de la plantilla seleccionada
const getTemplateContent = (templateType) => {
  if (templateType === 'structured') return EXAMEN_FISICO_TEMPLATE_HTML;
  if (templateType === 'neurologica') return EXAMEN_NEUROLOGICO_TEMPLATE_HTML;
  if (templateType === 'psiquiatrica') return EXAMEN_PSIQUIATRICO_TEMPLATE_HTML;
  return EXAMEN_FISICO_TEMPLATE_BLANK; // 'blank'
};

// Configuración compartida del editor Jodit (gratis, open source, sin límites de uso)
const JODIT_BASE_BUTTONS = [
  'undo', 'redo', '|',
  'bold', 'italic', 'underline', '|',
  'ul', 'ol', '|',
  'align', '|',
  'outdent', 'indent', '|',
  'eraser', '|',
  'source'
];

const AddConsultaPage = () => {

  const editorRef = useRef(null);
  const anamnesisEditorRef = useRef(null);

  // =================================================================
  // === 1. HOOKS (useState, useEffect, etc.) ===
  // =================================================================
  const { consultaId, id: pacienteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = Boolean(consultaId);
  const recognitionRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const transcriptBuffer = useRef('');
  const { selectedClinicId } = useClinic();
  const { memberships } = useClinic();
  // Lista de doctores en la clínica activa
  const clinicDoctors = React.useMemo(() => {
    if (!selectedClinicId || !Array.isArray(memberships)) return [];
    const sid = Number(selectedClinicId);
    const list = memberships
      .filter(m => Number(m?.clinica?.id) === sid && (m?.rol === 'Médico' || m?.rol === 'Medico'))
      .map(m => ({
        id: m.id,
        name: `${(m.usuario?.first_name || '').trim()} ${(m.usuario?.last_name || '').trim()}`.trim() || (m.usuario?.username || 'Médico'),
        userId: m.usuario?.id,
        role: m.rol,
    }));
    // Evitar incluir al mismo médico que atiende si puedes inferirlo
    // No siempre tenemos user.medico.id_medico aquí, pero dejamos todo por ahora
    return list;
  }, [memberships, selectedClinicId]);      

  // Configuración del editor Jodit para Anamnesis (memoizada para no recrear el editor en cada render)
  const joditConfigAnamnesis = React.useMemo(() => ({
    readonly: false,
    height: 500,
    toolbarAdaptive: false,
    buttons: JODIT_BASE_BUTTONS,
    buttonsMD: JODIT_BASE_BUTTONS,
    buttonsSM: JODIT_BASE_BUTTONS,
    buttonsXS: JODIT_BASE_BUTTONS,
    style: { font: '14px Helvetica, Arial, sans-serif' },
    hidePoweredByJodit: true,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_as_html',
  }), []);

  // Configuración del editor Jodit para Examen Físico
  const joditConfigExamenFisico = React.useMemo(() => ({
    readonly: false,
    height: 350,
    toolbarAdaptive: false,
    buttons: JODIT_BASE_BUTTONS,
    buttonsMD: JODIT_BASE_BUTTONS,
    buttonsSM: JODIT_BASE_BUTTONS,
    buttonsXS: JODIT_BASE_BUTTONS,
    style: { font: '14px Helvetica, Arial, sans-serif' },
    hidePoweredByJodit: true,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_as_html',
  }), []);

  const [formData, setFormData] = useState({
    paciente: pacienteId || null,
    presion_arterial: '', pulso: '', frecuencia_cardiaca: '', temperatura: '',
    saturacion_oxigeno: '', peso: '', altura: '', frecuencia_respiratoria: '', perimetro_cefalico: '', superficie_corporal: '',
    antecedentes_patologicos: '', antecedentes_alergicos: '', antecedentes_quirurgicos: '', antecedentes_toxicologicos: '',
    antecedentes_gineco_obstetricos: '', // Nuevo campo para antecedentes gineco-obstétricos
    antecedentes_psiquiatricos: '',      // Antecedentes psiquiátricos personales y familiares
    anamnesis: '', examen_fisico: EXAMEN_FISICO_TEMPLATE_HTML, // Plantilla predefinida
    diagnostico_principal: '',
    diagnostico_secundario: '', diagnostico_terciario: '', diagnostico_cuaternario: '', // Nuevo campo para 4to diagnóstico
    examenes_laboratoriales: '', examenes_imagen: '',
    plan_tratamiento: '',
    fecha_hora_consulta: new Date().toLocaleDateString('en-CA'), // Formato YYYY-MM-DD en zona horaria local
    imc: '',
  });

  // Unidades para peso y altura
  const [unidadPeso, setUnidadPeso] = useState('Kg');
  const [unidadAltura, setUnidadAltura] = useState('cm');
  const [unidadTemp, setUnidadTemp] = useState('°C')

  
  // Estado para paciente femenino
  const [esPacienteFemenino, setEsPacienteFemenino] = useState(false);
  const [esPediatrico, setEsPediatrico] = useState(false);

  // ===== Antecedentes Gineco-Obstetricos: campos estructurados (Opcion A) =====
  // Estos controles reconstruyen automáticamente el texto que se guarda en
  // formData.antecedentes_gineco_obstetricos. El médico igual puede editar el texto libre.
  const GINECO_FIELDS_INICIAL = {
    fum: '', fpp: '',
    gestas: '', partos: '', cesareas: '', abortos: '', hijos_vivos: '', hijos_muertos: '',
    menarquia: '', menopausia: '',
    ciclo_dias: '', ciclo_regularidad: '',
    dismenorrea: '',
    anticonceptivo: '',
    embarazos_previos: '',
    citologia_fecha: '', citologia_resultado: '',
    mamografia_fecha: '', mamografia_resultado: '',
    lactancia: '',
    obstetricos_relevantes: '',
  };
  const [ginecoFields, setGinecoFields] = useState(GINECO_FIELDS_INICIAL);

  // Opciones de los dropdowns
  const OPC_SI_NO = ['', 'Sí', 'No'];
  const OPC_REGULARIDAD = ['', 'Regular', 'Irregular'];
  const OPC_ANTICONCEPTIVO = ['', 'Ninguno', 'ACO (anticonceptivos orales)', 'DIU de cobre', 'DIU hormonal (SIU)', 'Implante subdérmico', 'Inyectable', 'Parche transdérmico', 'Anillo vaginal', 'Preservativo', 'Ligadura de trompas', 'Vasectomía (pareja)', 'Método natural', 'Otro'];
  const OPC_CITOLOGIA = ['', 'Normal / Negativa', 'ASCUS', 'ASC-H', 'LSIL', 'HSIL', 'AGC', 'Carcinoma', 'Insatisfactoria', 'No realizada'];
  const OPC_MAMOGRAFIA = ['', 'BIRADS 0 (incompleto)', 'BIRADS 1 (normal)', 'BIRADS 2 (benigno)', 'BIRADS 3 (probablemente benigno)', 'BIRADS 4 (sospechoso)', 'BIRADS 5 (alta sospecha)', 'BIRADS 6 (malignidad conocida)', 'No realizada'];

  // Construye el texto de la plantilla a partir de los campos estructurados
  const buildGinecoTemplate = (f) => {
    const v = (x) => (x === undefined || x === null ? '' : String(x));
    return (
`FUM (Fecha Última Menstruación): ${v(f.fum)}
FPP (Regla de Naegele): ${v(f.fpp)}
Gestas (G): ${v(f.gestas)}    Partos (P): ${v(f.partos)}    Cesáreas (C): ${v(f.cesareas)}    Abortos (A): ${v(f.abortos)}    Hijos Vivos (HV): ${v(f.hijos_vivos)}    Hijos Muertos (HM): ${v(f.hijos_muertos)}

Menarquia: ${v(f.menarquia)}
Menopausia: ${v(f.menopausia)}
Ciclo: ${v(f.ciclo_dias)} días - ${v(f.ciclo_regularidad)}
Dismenorrea: ${v(f.dismenorrea)}

Método anticonceptivo actual: ${v(f.anticonceptivo)}
Embarazos previos y desenlaces:
${v(f.embarazos_previos)}

Citología (Papanicolaou): ${v(f.citologia_resultado)}    Fecha: ${v(f.citologia_fecha)}
Mamografía: ${v(f.mamografia_resultado)}    Fecha: ${v(f.mamografia_fecha)}

Lactancia actual: ${v(f.lactancia)}

Antecedentes obstétricos relevantes:
${v(f.obstetricos_relevantes)}
`
    );
  };

  // Parser inverso: lee el texto guardado y rellena los campos estructurados (modo edición)
  const parseGinecoTemplate = (text) => {
    const f = { ...GINECO_FIELDS_INICIAL };
    if (!text || typeof text !== 'string') return f;

    // Helper: captura lo que sigue a una etiqueta hasta fin de línea
    const grab = (re) => {
      const m = text.match(re);
      return m && m[1] !== undefined ? m[1].trim() : '';
    };
    // Helper: normaliza un valor contra una lista de opciones (case-insensitive, sin acentos)
    const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    const match = (val, options) => {
      const nv = norm(val);
      if (!nv) return '';
      // 1) Coincidencia exacta
      const exact = options.find(o => o && norm(o) === nv);
      if (exact) return exact;
      // 2) El valor contiene la opción completa -> elegir la opción MÁS LARGA (más específica)
      const contained = options
        .filter(o => o && norm(o).length > 0 && nv.includes(norm(o)))
        .sort((a, b) => norm(b).length - norm(a).length);
      if (contained.length) return contained[0];
      // 3) Alguna opción contiene el valor -> la más corta (más cercana)
      const containers = options
        .filter(o => o && norm(o).includes(nv))
        .sort((a, b) => norm(a).length - norm(b).length);
      if (containers.length) return containers[0];
      return val.trim();
    };

    f.fum = grab(/FUM[^:]*:\s*([^\n\r]*)/i);
    f.fpp = grab(/FPP[^:]*:\s*([^\n\r]*)/i);
    f.gestas = grab(/Gestas\s*\(G\):\s*([^\s]*)/i);
    f.partos = grab(/Partos\s*\(P\):\s*([^\s]*)/i);
    f.cesareas = grab(/Ces[aá]reas\s*\(C\):\s*([^\s]*)/i);
    f.abortos = grab(/Abortos\s*\(A\):\s*([^\s]*)/i);
    f.hijos_vivos = grab(/Hijos Vivos\s*\(HV\):\s*([^\s]*)/i);
    f.hijos_muertos = grab(/Hijos Muertos\s*\(HM\):\s*([^\s\n\r]*)/i);
    f.menarquia = grab(/Menarquia:\s*([^\n\r]*)/i);
    f.menopausia = grab(/Menopausia:\s*([^\n\r]*)/i);

    const ciclo = grab(/Ciclo:\s*([^\n\r]*)/i);
    if (ciclo) {
      const dm = ciclo.match(/([0-9]+)/);
      f.ciclo_dias = dm ? dm[1] : '';
      f.ciclo_regularidad = match(ciclo, OPC_REGULARIDAD);
    }
    f.dismenorrea = match(grab(/Dismenorrea:\s*([^\n\r]*)/i), OPC_SI_NO);
    f.anticonceptivo = match(grab(/M[eé]todo anticonceptivo actual:\s*([^\n\r]*)/i), OPC_ANTICONCEPTIVO);

    // Embarazos previos: la(s) línea(s) entre "Embarazos previos..." y "Citología"
    const emb = text.match(/Embarazos previos y desenlaces:\s*([\s\S]*?)(?:Citolog[ií]a|$)/i);
    if (emb && emb[1]) f.embarazos_previos = emb[1].trim();

    const cito = grab(/Citolog[ií]a\s*\(Papanicolaou\):\s*([^\n\r]*)/i);
    if (cito) {
      const fechaM = cito.match(/Fecha:\s*([^\n\r]*)/i);
      f.citologia_fecha = fechaM ? fechaM[1].trim() : '';
      const resultado = cito.replace(/\s*Fecha:.*/i, '').trim();
      f.citologia_resultado = match(resultado, OPC_CITOLOGIA);
    }
    const mamo = grab(/Mamograf[ií]a:\s*([^\n\r]*)/i);
    if (mamo) {
      const fechaM = mamo.match(/Fecha:\s*([^\n\r]*)/i);
      f.mamografia_fecha = fechaM ? fechaM[1].trim() : '';
      const resultado = mamo.replace(/\s*Fecha:.*/i, '').trim();
      f.mamografia_resultado = match(resultado, OPC_MAMOGRAFIA);
    }

    f.lactancia = match(grab(/Lactancia actual:\s*([^\n\r]*)/i), OPC_SI_NO);

    const obst = text.match(/Antecedentes obst[eé]tricos relevantes:\s*([\s\S]*)$/i);
    if (obst && obst[1]) f.obstetricos_relevantes = obst[1].trim();

    return f;
  };

  // Handler de cualquier campo estructurado: actualiza el campo y reconstruye el texto
  const handleGinecoFieldChange = (name, value) => {
    setGinecoFields(prev => {
      const next = { ...prev, [name]: value };
      setFormData(fd => ({ ...fd, antecedentes_gineco_obstetricos: buildGinecoTemplate(next) }));
      return next;
    });
  };

  const [medicamentos, setMedicamentos] = useState([]);

  // Estado para panel "Receta anterior"
  const [ultimaConsulta, setUltimaConsulta] = useState(null);      // { found, fecha, diagnostico, diagnostico_secundario, medicamentos }
  const [loadingUltimaMeds, setLoadingUltimaMeds] = useState(false);
  const [panelRecetaAnterior, setPanelRecetaAnterior] = useState(false);
  const [selectedMedsAnteriores, setSelectedMedsAnteriores] = useState({}); // { index: bool }
  const [openMedicamentoModal, setOpenMedicamentoModal] = useState(false);
  const [currentMedicamento, setCurrentMedicamento] = useState(null);
  // Lista de etiquetas de receta detectadas a partir de los medicamentos cargados en el formulario
  const recetaTags = React.useMemo(() => {
    const tags = new Set();
    (medicamentos || []).forEach(m => {
      const tag = (m.receta_tag || 'general').trim().toLowerCase();
      if (tag) tags.add(tag);
    });
    return Array.from(tags);
  }, [medicamentos]);

  // Tag seleccionado para envío de receta individual
  const [selectedTagToEmail, setSelectedTagToEmail] = useState('general');

  const [isEditMedicamentoMode, setIsEditMedicamentoMode] = useState(false);
  const [editMedicamentoIndex, setEditMedicamentoIndex] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [pacienteNombre, setPacienteNombre] = useState('');
  
  const [loadingIcd, setLoadingIcd] = useState(false);
  const [icdOptions, setIcdOptions] = useState([]);
  const [diagnosticoPrincipalInputValue, setDiagnosticoPrincipalInputValue] = useState('');
  const [diagnosticoSecundarioInputValue, setDiagnosticoSecundarioInputValue] = useState('');
  const [diagnosticoTerciarioInputValue, setDiagnosticoTerciarioInputValue] = useState('');
  const [diagnosticoCuaternarioInputValue, setDiagnosticoCuaternarioInputValue] = useState('');
  
  const [catalogoExamenes, setCatalogoExamenes] = useState([]);
  const [catalogoMedicamentos, setCatalogoMedicamentos] = useState([]);
  const [sendingRecipe, setSendingRecipe] = useState(false)

  const [cimaOptions, setCimaOptions] = useState([]);
  const [loadingCima, setLoadingCima] = useState(false);
  const drugSearchControllerRef = React.useRef(null);

  const [deriveOpen, setDeriveOpen] = useState(false);
  // Modal Interconsulta (modo doctor solo)
  const [interconsultaOpen, setInterconsultaOpen] = useState(false);
  const [interconsultaData, setInterconsultaData] = useState({
    nombreDoctor: '',
    especialidad: '',
    historiaEnfermedad: '',
    tratamiento: '',
    email: '',
  });
  const [generandoRefPDF, setGenerandoRefPDF] = useState(false);
  const [enviandoRefEmail, setEnviandoRefEmail] = useState(false);
  const [deriveDoctor, setDeriveDoctor] = useState(null);
  const [deriveReason, setDeriveReason] = useState('');
  const [ordenesEnfermeria, setOrdenesEnfermeria] = useState('');
  
  // Modal Examen Psiquiátrico
  const [psiquiatricoOpen, setPsiquiatricoOpen] = useState(false);
  const [psiquiatricoSelections, setPsiquiatricoSelections] = useState({
    apariencia: [],
    conciencia: [],
    lenguaje: [],
    animo: [],
    pensamiento: [],
    percepcion: [],
    cognicion: [],
    insight: []
  });
  


  // Estado para la plantilla seleccionada
const [selectedTemplate, setSelectedTemplate] = useState('structured');  

  // Calcula Superficie Corporal (m²) según Mosteller a partir de peso/altura y sus unidades
  const computeBSA = (pesoValue, unidadPesoLocal, alturaValue, unidadAlturaLocal) => {
    const toNumber = (v) => {
      if (v === undefined || v === null) return NaN
      return parseFloat(String(v).replace(',', '.'))
    }

    let peso = toNumber(pesoValue)
    let altura = toNumber(alturaValue)
    if (isNaN(peso) || isNaN(altura) || peso <= 0 || altura <= 0) return ''

    // Convertir peso a kg
    if (unidadPesoLocal === 'Lb') {
      peso = peso * 0.45359237
    } // si es Kg, ya está bien

    // Convertir altura a cm
    if (unidadAlturaLocal === 'm') {
      altura = altura * 100
    } else if (unidadAlturaLocal === 'ft') {
      altura = altura * 30.48
    } // si es cm, ya está bien

    const sc = Math.sqrt((altura * peso) / 3600) // m²
    if (!isFinite(sc)) return ''
    return sc.toFixed(2) // dos decimales
  }

  const computeBMI = (pesoValue, unidadPesoLocal, alturaValue, unidadAlturaLocal) => {
    const toNumber = (v) => {
      if (v === undefined || v === null) return NaN
      return parseFloat(String(v).replace(',', '.'))
    }
    let peso = toNumber(pesoValue)
    let altura = toNumber(alturaValue)
    if (isNaN(peso) || isNaN(altura) || peso <= 0 || altura <= 0) return ''

    // peso a kg
    if (unidadPesoLocal === 'Lb') peso = peso * 0.45359237
    // altura a metros
    if (unidadAlturaLocal === 'cm') altura = altura / 100
    else if (unidadAlturaLocal === 'ft') altura = altura * 0.3048
    // si es 'm', ya está

    const bmi = peso / (altura * altura)
    if (!isFinite(bmi)) return ''
    return bmi.toFixed(2)
  }


// Recalcular cuando peso/altura o sus unidades cambien
useEffect(() => {
  const bsa = computeBSA(formData.peso, unidadPeso, formData.altura, unidadAltura)
  const bmi = computeBMI(formData.peso, unidadPeso, formData.altura, unidadAltura)
  setFormData(prev => ({ ...prev, superficie_corporal: bsa, imc: bmi }))
}, [formData.peso, formData.altura, unidadPeso, unidadAltura])


const fetchInitialData = useCallback(async () => {
  setLoading(true);
  try {
    let effectivePacienteId = pacienteId;
    let initialConsultaData = null;

    // --- PASO 1: Determinar el ID del paciente y obtener datos de la consulta si estamos en modo edición ---
    if (isEditMode) {
      const consultaRes = await axiosInstance.get(`/consultas/${consultaId}/`);
      initialConsultaData = consultaRes.data;
      if (!effectivePacienteId || effectivePacienteId === 'undefined') {
        effectivePacienteId = initialConsultaData.paciente;
      }
    }

    // Si después de todo no tenemos un ID de paciente, es un error.
    if (!effectivePacienteId || effectivePacienteId === 'undefined') {
      toast.error('ID de paciente no válido.');
      navigate(-1);
      return;
    }

    // --- PASO 2: Obtener el resto de los datos necesarios ---
    const [examenesRes, medicamentosRes, pacienteRes] = await Promise.all([
      axiosInstance.get('/catalogo-examenes/'),
      axiosInstance.get('/catalogo-medicamentos/'),
      axiosInstance.get(`/pacientes/${effectivePacienteId}/`),
    ]);
    
    const sexo = (pacienteRes.data.sexo || '').toString()
    const isFemale = sexo.trim().toLowerCase().startsWith('fem')
    setEsPacienteFemenino(isFemale)

    // --- PASO 3: Actualizar el estado de la aplicación con todos los datos ---
    setCatalogoExamenes(examenesRes.data);
    setCatalogoMedicamentos(medicamentosRes.data);
    setPacienteNombre(`${pacienteRes.data.nombres} ${pacienteRes.data.apellidos}`);
    setEsPacienteFemenino(pacienteRes.data.sexo === 'Femenino');
    // Determinar si es paciente pediátrico (<= 19 años) para mostrar las Tablas de Crecimiento
    if (pacienteRes.data.fecha_nacimiento) {
      const nac = new Date(pacienteRes.data.fecha_nacimiento);
      const edadAnios = (new Date() - nac) / (1000 * 60 * 60 * 24 * 365.25);
      setEsPediatrico(edadAnios >= 0 && edadAnios <= 19);
    }

    if (isEditMode && initialConsultaData) {
      // --- Lógica para EDITAR una consulta ---
      const data = { ...initialConsultaData };

      if (data.fecha_hora_consulta) {
        data.fecha_hora_consulta = new Date(data.fecha_hora_consulta)
      .toLocaleDateString("en-CA");
    }

    // NUEVO: si es femenina y el campo está vacío, precargar plantilla
    if (
      isFemale &&
      (!data.antecedentes_gineco_obstetricos || String(data.antecedentes_gineco_obstetricos).trim() === '')
    ) {
      data.antecedentes_gineco_obstetricos = GINECO_OBSTETRICO_TEMPLATE;
    }

    // Si el examen físico viene vacío o nulo, lo inicializamos con la plantilla en blanco.
    if (!data.examen_fisico || data.examen_fisico.trim() === '') {
      data.examen_fisico = EXAMEN_FISICO_TEMPLATE_BLANK;
      setSelectedTemplate('blank');
    }

    // Extraer unidades de peso y altura
    if (data.peso && data.peso.includes(' ')) {
      const [valor, unidad] = data.peso.split(' ');
      data.peso = valor;
      setUnidadPeso(unidad);
    }
    if (data.altura && data.altura.includes(' ')) {
      const [valor, unidad] = data.altura.split(' ');
      data.altura = valor;
      setUnidadAltura(unidad);
    }

    if (data.temperatura && typeof data.temperatura === 'string') {
      const m = data.temperatura.match(/^\s*([0-9]+(?:[.,][0-9]+)?)\s*(°?[CF])\s*$/i)
      if (m) {
        data.temperatura = m[1]
        const unit = m[2].toUpperCase()
        setUnidadTemp(unit === 'C' || unit === '°C' ? '°C' : '°F')
      } else {
        setUnidadTemp('°C')
      }
    }

    if (data.saturacion_oxigeno && /%$/.test(String(data.saturacion_oxigeno).trim())) {
      data.saturacion_oxigeno = String(data.saturacion_oxigeno).trim().replace(/\s*%$/, '')
    }

    // Superficie corporal si existía, dejar solo el número
    if (data.superficie_corporal && /m²$/i.test(String(data.superficie_corporal))) {
      data.superficie_corporal = String(data.superficie_corporal).replace(/\s*m²$/i, '')
    }

      const normalizeStr = (v) => (v === null || v === undefined ? '' : String(v));

      [
        'presion_arterial', 'pulso', 'frecuencia_cardiaca', 'frecuencia_respiratoria',
        'temperatura', 'saturacion_oxigeno', 'peso', 'altura', 'perimetro_cefalico',
        'antecedentes_patologicos', 'antecedentes_alergicos', 'antecedentes_quirurgicos', 'antecedentes_toxicologicos',
        'antecedentes_gineco_obstetricos', 'antecedentes_psiquiatricos', 'anamnesis', 'examen_fisico',
        'plan_tratamiento', 'examenes_laboratoriales', 'examenes_imagen', 'imc', 'superficie_corporal',
      ].forEach(k => { data[k] = normalizeStr(data[k]); });

      // Rellenar los controles estructurados gineco-obstétricos a partir del texto guardado
      if (isFemale && data.antecedentes_gineco_obstetricos) {
        setGinecoFields(parseGinecoTemplate(data.antecedentes_gineco_obstetricos));
      }

      setFormData(data);
      setMedicamentos(data.medicamentos || []);
      setDiagnosticoPrincipalInputValue(data.diagnostico_principal || '');
      setDiagnosticoSecundarioInputValue(data.diagnostico_secundario || '');
      setDiagnosticoTerciarioInputValue(data.diagnostico_terciario || '');
      setDiagnosticoCuaternarioInputValue(data.diagnostico_cuaternario || '');

    } else {
      // --- Lógica para CREAR una nueva consulta ---
      setFormData(prev => ({
        ...prev,
        paciente: effectivePacienteId,
        examen_fisico: EXAMEN_FISICO_TEMPLATE_HTML,
        antecedentes_gineco_obstetricos: isFemale ? GINECO_OBSTETRICO_TEMPLATE : '', // Empezar con la plantilla estructurada
      }));
      setSelectedTemplate('structured');
    }

  } catch (error) {
    toast.error('Error al cargar los datos iniciales.');
    console.error("Error detallado en fetchInitialData:", error);
    navigate(-1);
  } finally {
    setLoading(false);
    setTimeout(() => setFadeIn(true), 100);
  }
  // El array de dependencias debe ser simple para que se ejecute solo una vez al montar.
}, [consultaId, pacienteId, isEditMode, navigate]);

// Función para manejar cambios en el editor
  const handleEditorChange = (content, editor) => {
   setFormData(prev => ({ ...prev, examen_fisico: content }));
};

  const handleAnamnesisChange = (content) => {
  setFormData(prev => ({ ...prev, anamnesis: content }));
};

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Fetch automático de medicamentos de la última consulta (solo en modo nueva consulta)
  useEffect(() => {
    if (isEditMode || !pacienteId) return;
    const fetchUltimaConsulta = async () => {
      setLoadingUltimaMeds(true);
      try {
        const res = await axiosInstance.get(
          `/consultas/paciente/${pacienteId}/ultima-consulta-medicamentos/`
        );
        if (res.data.found && res.data.medicamentos.length > 0) {
          setUltimaConsulta(res.data);
        } else {
          setUltimaConsulta(null);
        }
      } catch (err) {
        // Silencioso — no interrumpe la consulta si falla
        setUltimaConsulta(null);
      } finally {
        setLoadingUltimaMeds(false);
      }
    };
    fetchUltimaConsulta();
  }, [pacienteId, isEditMode]);

  const searchICD = useCallback(
    debounce(async (query, setOptions) => {
      if (query.length < 3) { 
        setIcdOptions([]);
        return; 
      }
      setLoadingIcd(true);
      try {
        const response = await axiosInstance.get('icd-search/', { params: { q: query } });
        setIcdOptions(response.data.destinationEntities || []);
      } catch (error) { 
        console.error('Error buscando en CIE-11:', error); 
      } finally {
        setLoadingIcd(false); 
      }
    }, 500), []);

  const searchDrugs = useCallback(
    debounce(async (query) => {
      if ((query || '').length < 3) {
        setCimaOptions([]);
        return;
      }

      // Cancelar request anterior si existe
      if (drugSearchControllerRef.current) {
        try { drugSearchControllerRef.current.abort(); } catch {}
      }
      drugSearchControllerRef.current = new AbortController();

      setLoadingCima(true);
      try {
        const response = await axiosInstance.get(`/fda-search/?search=${encodeURIComponent(query)}`, {
          timeout: 15000,
          signal: drugSearchControllerRef.current.signal,
        });
        const raw = Array.isArray(response.data) ? response.data : [];

        // Normalizar y deduplicar por (source + nombre + dosis)
        const seen = new Set();
        const results = [];
        for (const med of raw) {
          const item = {
            ...med,
            source: med.source || 'openFDA',
            nombre: (med.nombre || '').trim(),
            dosis: (med.dosis || '').trim(),
          };
          const key = `${item.source}|${item.nombre.toLowerCase()}|${item.dosis.toLowerCase()}`;
          if (!seen.has(key)) {
            seen.add(key);
            results.push(item);
          }
        }
        setCimaOptions(results);
      } catch (error) {
        // Ignorar cancelaciones
        if (axios.isCancel?.(error) || error.code === 'ERR_CANCELED') {
          return;
        }
        // Ignorar timeouts mostrando simplemente una lista vacía
        if (error.code === 'ECONNABORTED') {
          console.warn('Búsqueda de medicamentos: timeout');
          setCimaOptions([]);
          return;
        }
        console.error('Error buscando medicamentos:', error);
        setCimaOptions([]);
      } finally {
        setLoadingCima(false);
      }
    }, 500),
    []
  );


  // =================================================================
  // === 2. HANDLER FUNCTIONS ===
  // =================================================================

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleAutocompleteChange = (event, newValue) => {
    let diagnosisName = '';
    if (typeof newValue === 'string') {
      diagnosisName = newValue;
    } else if (newValue && newValue.title) {
      diagnosisName = stripHtmlTags(newValue.title);
    }
    setDiagnosticoPrincipalInputValue(diagnosisName);
    setFormData(prev => ({ ...prev, diagnostico_principal: diagnosisName }));
  };

  const handleDiagnosticoChange = (event, newValue, inputSetter, formFieldName) => {
  let diagnosisName = '';
  if (typeof newValue === 'string') {
    diagnosisName = newValue;
  } else if (newValue && newValue.title) {
    diagnosisName = stripHtmlTags(newValue.title);
  }
  inputSetter(diagnosisName);
  setFormData(prev => ({ ...prev, [formFieldName]: diagnosisName }));
};


  const handleSubmit = async (e) => {
  e.preventDefault();

  // Obtener la fecha del formulario y la hora actual
  const selectedDate = formData.fecha_hora_consulta; // YYYY-MM-DD
  const now = new Date();
  const currentDateString = now.toLocaleDateString("en-CA"); // Fecha actual en formato YYYY-MM-DD
  
  // Validar que la fecha no sea futura
  if (selectedDate > currentDateString) {
    toast.error("No se pueden guardar consultas con fechas futuras");
    return;
  }
  
  // Obtener hora y minutos actuales
  const hours = now.getHours().toString().padStart(2, '0');
  const minutes = now.getMinutes().toString().padStart(2, '0');
  
  // Combinar fecha seleccionada con hora actual
  const combinedDateTime = `${selectedDate}T${hours}:${minutes}:00`;
  
  setSaving(true);
  if (!user || !user.medico?.id_medico) {
    toast.error("No se ha podido identificar al médico.");
    setSaving(false);
    return;
  }
  
  try {
    // Primero, si estamos en modo edición, obtener los datos actuales de la consulta
    let consultaActual = null;
    if (isEditMode) {
      try {
        const respuestaConsulta = await axiosInstance.get(`/consultas/${consultaId}/`);
        consultaActual = respuestaConsulta.data;
        console.log("Datos actuales de la consulta:", consultaActual);
      } catch (error) {
        console.error("Error al obtener datos actuales de la consulta:", error);
      }
    }

    // Mezclar órdenes de enfermería dentro del plan_tratamiento, si existen
    let planTratamientoFinal = formData.plan_tratamiento || '';
    if ((ordenesEnfermeria || '').trim()) {
      const block = `\n\nÓrdenes de Enfermería:\n${ordenesEnfermeria.trim()}`;
      planTratamientoFinal += block;
    }

    const ns = v => (v === null || v === undefined) ? '' : String(v);
    // Preparar los datos comunes
    const datosComunes = {
      fecha_hora_consulta: combinedDateTime,
      peso: formData.peso ? `${formData.peso} ${unidadPeso}` : '',
      altura: formData.altura ? `${formData.altura} ${unidadAltura}` : '',
      diagnostico_principal: diagnosticoPrincipalInputValue,
      diagnostico_secundario: diagnosticoSecundarioInputValue,
      diagnostico_terciario: diagnosticoTerciarioInputValue,
      diagnostico_cuaternario: diagnosticoCuaternarioInputValue,
      presion_arterial: formData.presion_arterial || '',
      pulso: formData.pulso || '',
      frecuencia_cardiaca: formData.frecuencia_cardiaca || '',
      frecuencia_respiratoria: formData.frecuencia_respiratoria || '',
      perimetro_cefalico: formData.perimetro_cefalico || '',
      temperatura: formData.temperatura ? `${formData.temperatura} ${unidadTemp}` : '',
      saturacion_oxigeno: ensureWithUnit(formData.saturacion_oxigeno, '%'),
      antecedentes_patologicos: formData.antecedentes_patologicos || '',
      antecedentes_alergicos: formData.antecedentes_alergicos || '',
      antecedentes_toxicologicos: formData.antecedentes_toxicologicos || '', 
      antecedentes_quirurgicos: formData.antecedentes_quirurgicos || '',
      antecedentes_gineco_obstetricos: formData.antecedentes_gineco_obstetricos || '',
      antecedentes_psiquiatricos: formData.antecedentes_psiquiatricos || '',
      anamnesis: formData.anamnesis || '',
      examen_fisico: formData.examen_fisico || '',
      plan_tratamiento: planTratamientoFinal || '',
      examenes_laboratoriales: formData.examenes_laboratoriales || '',
      examenes_imagen: formData.examenes_imagen || '',
      superficie_corporal: formData.superficie_corporal
        ? `${formData.superficie_corporal} m²`
        : '',
      imc: formData.imc ? `${formData.imc} kg/m²` : '', 
    };
    
    let response;

    const extraConfig = selectedClinicId ? { headers: { 'X-Clinic-Id': String(selectedClinicId) } } : { meta: { skipClinicHeader: true } };
    
    if (isEditMode) {
      // MODO EDICIÓN: Usar PATCH en lugar de PUT para actualizar solo los campos que enviamos
      // INCLUYE los medicamentos en el mismo objeto
      const datosConMedicamentos = {
        ...datosComunes,
        medicamentos: medicamentos
        };

      console.log('Enviando datos para edición:', datosConMedicamentos);
       response = await axiosInstance.patch(`/consultas/${consultaId}/`, datosConMedicamentos, extraConfig);
      
    } else {
      // MODO CREACIÓN: Incluir todos los campos necesarios
      const datosCreacion = {
        ...datosComunes,
        paciente: parseInt(pacienteId, 10),
        medico: user.medico.id_medico,
        medicamentos: medicamentos,
      };
      
      console.log('Enviando datos para creación:', datosCreacion);
      response = await axiosInstance.post('/consultas/', datosCreacion, extraConfig);
    }
    
    toast.success(`Consulta ${isEditMode ? 'actualizada' : 'creada'} correctamente.`);
    navigate(`/pacientes/${pacienteId}`);
  } catch (error) {
    console.error('Error al guardar la consulta:', error.response?.data);
    
    // Mostrar mensaje de error detallado
      if (error.response?.data) {
        const errorData = error.response.data;
        let errorMessage = 'Errores de validación:';
        
        if (typeof errorData === 'object') {
          Object.entries(errorData).forEach(([field, errors]) => {
            const errorText = Array.isArray(errors) ? errors.join(', ') : errors;
            errorMessage += `\n- ${field}: ${errorText}`;
          });
        } else {
          errorMessage = `Error: ${JSON.stringify(errorData)}`;
        }
        
        toast.error(errorMessage);
      } else {
        toast.error('Hubo un error al guardar la consulta.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleOpenMedicamentoModal = (medicamento = null, index = null) => {
    if (medicamento) {
      setCurrentMedicamento(medicamento);
      setIsEditMedicamentoMode(true);
      setEditMedicamentoIndex(index);
    } else {
      setCurrentMedicamento({ nombre: '', nombre_comercial: '', dosis: '', frecuencia: '', duracion: '', notas: '' });
      setIsEditMedicamentoMode(false);
    }
    setOpenMedicamentoModal(true);
  };

  const handleCloseMedicamentoModal = () => setOpenMedicamentoModal(false);
  
  const handleMedicamentoInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentMedicamento(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveMedicamento = () => {
    if (!currentMedicamento.nombre) {
      toast.error('El nombre del medicamento es requerido.');
      return;
    }
    if (isEditMedicamentoMode) {
      const updatedMedicamentos = [...medicamentos];
      updatedMedicamentos[editMedicamentoIndex] = currentMedicamento;
      setMedicamentos(updatedMedicamentos);
    } else {
      setMedicamentos(prev => [...prev, currentMedicamento]);
    }
    handleCloseMedicamentoModal();
  };

  const handleDeleteMedicamento = (indexToDelete) => {
    if (window.confirm('¿Estás seguro de que quieres quitar este medicamento?')) {
      setMedicamentos(prev => prev.filter((_, index) => index !== indexToDelete));
    }
  };


// Función para manejar el cambio de plantilla
// Dentro de AddConsultaPage

const handleTemplateChange = (event) => {
  const newTemplate = event.target.value;
  
  // Si selecciona psiquiatrica, abrir el diálogo en lugar de cambiar el contenido
  if (newTemplate === 'psiquiatrica') {
    setPsiquiatricoOpen(true);
    return; // No cambiar el selectedTemplate
  }
  
  setSelectedTemplate(newTemplate);

  const currentContent = formData.examen_fisico.trim();
  const isPristine = currentContent === '' || currentContent === '<p></p>' || currentContent === EXAMEN_FISICO_TEMPLATE_HTML;

  // Si el editor está vacío/prístino, o si el usuario confirma, cambiamos la plantilla.
  if (isPristine || window.confirm('¿Deseas reemplazar el contenido actual con la nueva plantilla?')) {
    const newContent = getTemplateContent(newTemplate);
    setFormData(prev => ({ ...prev, examen_fisico: newContent }));
    
    // Actualiza el editor directamente si ya está montado
    if (editorRef.current) {
      editorRef.current.value = newContent;
    }
  } else {
      // Si el usuario cancela, revertimos la selección en el dropdown
      setSelectedTemplate(newTemplate === 'structured' ? 'blank' : 'structured');
  }
};

  const handleResetTemplate = () => {
    const newContent = getTemplateContent(selectedTemplate);
    setFormData(prev => ({ ...prev, examen_fisico: newContent }));
    if (editorRef.current) {
      editorRef.current.value = newContent;
    }
    toast.info(`Plantilla ${selectedTemplate} restablecida.`);
  };

  const handleSendRecipeEmail = async () => {
    if (!isEditMode) return
    setSendingRecipe(true)
    try {
      await axiosInstance.post(`/consultas/${consultaId}/enviar_receta/`)
      toast.success('Receta enviada al email del paciente')
    } catch (error) {
      const msg = error.response?.data?.error || 'No se pudo enviar la receta'
      toast.error(msg)
    } finally {
      setSendingRecipe(false)
    }
  }

  const ensureWithUnit = (value, unit) => {
    if (!value) return ''
    const val = String(value).trim()
    return /\d/.test(val) && !val.endsWith(unit) ? `${val} ${unit}` : val
  }

  const handleResetGinecoTemplate = () => {
    setGinecoFields(GINECO_FIELDS_INICIAL);
    setFormData(prev => ({ ...prev, antecedentes_gineco_obstetricos: GINECO_OBSTETRICO_TEMPLATE }));
    toast.info('Plantilla de Antecedentes Gineco-Obstétricos restablecida.');
  };

  const buildOptionKey = (opt) => {
      if (typeof opt === 'string') return `str|${opt}`;
    const src = (opt?.source || 'local').toLowerCase();
    const id = String(opt?.id ?? '').toLowerCase();
    const nombre = String(opt?.nombre ?? '').toLowerCase().trim();
    const dosis = String(opt?.dosis ?? '').toLowerCase().trim();
    return `${src}|${id}|${nombre}|${dosis}`;
  };

  const [isProcessingVoice, setIsProcessingVoice] = useState(false);

  const handleProcesarVoz = async (textoTranscrito) => {
    // Validación de seguridad
    if (!textoTranscrito || typeof textoTranscrito !== 'string') {
      toast.error("No se capturó audio válido.");
      return;
    }

    // Opcional: Mostrar un toast de "Pensando..."
    const toastId = toast.loading("La IA está redactando la HEA...");

    try {
      const response = await axiosInstance.post('procesar-hea-ai/', {
        texto_bruto: textoTranscrito
      });
      
      setFormData(prev => {
        // Concatenamos por si ya había escrito algo antes
        const nuevoContenido = (prev.anamnesis || '') + ' ' + response.data.hea_redactada;
        // Actualizar el editor directamente (Jodit no re-sincroniza solo con el prop value)
        if (anamnesisEditorRef.current) {
          anamnesisEditorRef.current.value = nuevoContenido;
        }
        return { ...prev, anamnesis: nuevoContenido };
      });
      
      toast.update(toastId, { render: "HEA redactada con éxito", type: "success", isLoading: false, autoClose: 3000 });
    } catch (error) {
      console.error(error);
      toast.update(toastId, { render: "Error al conectar con la IA", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  // Estado para saber si está escuchando
  
  const [textoAcumulado, setTextoAcumulado] = useState('');

  // Función que activa el micrófono del navegador
  const handleToggleMic = () => {
    // CASO 1: Si ya estamos escuchando, DETENER.
    if (isListening) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);

      // Verificamos si hay algo que enviar
    const textoFinal = transcriptBuffer.current.trim();
    if (textoFinal.length > 5) { // Mínimo 5 caracteres para evitar ruidos
        toast.info("Procesando historia completa...");
        handleProcesarVoz(textoFinal); // <--- ENVIAMOS TODO DE UNA VEZ
    } else {  
        toast.warning("Audio muy corto o vacío.");
    }

    // Limpiamos el buffer para la próxima
    transcriptBuffer.current = '';
    return;
    }

    // CASO 2: Iniciar escucha
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Navegador no soportado. Usa Chrome.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'es-HN';
    recognition.continuous = true; // <--- ESTO permite hablar seguido sin cortes
    recognition.interimResults = true;

    recognition.onstart = () => {
      setIsListening(true);
      transcriptBuffer.current = ''; // Aseguramos buffer limpio
      toast.info("Dictando... (Habla corrido, el texto se procesará al final) (Click de nuevo para finalizar)");
    };

    recognition.onresult = (event) => {
      let interimString = '';
      let finalString = '';

      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalString += event.results[i][0].transcript;
        } else {  
          interimString += event.results[i][0].transcript;
        }
      }

      // Acumulamos solo lo que ya es definitivo en nuestro buffer invisible
      if (finalString) {
        transcriptBuffer.current += ' ' + finalString;
        console.log("Buffer actual:", transcriptBuffer.current); // Para que veas en consola
      }
    };
    
    recognition.onerror = (event) => {
      // Si es 'no-speech' (silencio), NO hacemos nada, dejamos que siga escuchando
      if (event.error !== 'no-speech') {
        console.warn("Error speech:", event.error);
        setIsListening(false);
      }
    };

    recognition.onend = () => {
      // Ojo: En modo continuous, el onend a veces se dispara solo si hay mucho silencio o error de red.
      // Si el usuario NO ha dado click en parar (isListening sigue true), intentamos reiniciar
      // o simplemente asumimos que terminó. Para esta versión simple, asumiremos que terminó.
      if (isListening) {
        setIsListening(false);
        // Opcional: Auto-enviar si se corta solo
        if (transcriptBuffer.current.length > 5) {
          handleProcesarVoz(transcriptBuffer.current);
          transcriptBuffer.current = '';
        }  
      }  
    };
      
    recognitionRef.current = recognition;
    recognition.start();
  };


  // =================================================================
  // === 3. EARLY RETURN ===
  // =================================================================
  
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '80vh' 
      }}>
        <CircularProgress size={60} thickness={4} color="primary" />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          {isEditMode ? 'Cargando consulta...' : 'Preparando formulario...'}
        </Typography>
      </Box>
    );
  }

  // =================================================================
  // === 4. RENDER JSX ===
  // =================================================================

  return (
    <Box p={{ xs: 2, md: 4 }} sx={{ maxWidth: '1600px', margin: '0 auto' }}>
      {/* Botón para volver */}
      <Button 
        variant="text" 
        component={RouterLink} 
        to={`/pacientes/${pacienteId}`} 
        startIcon={<ArrowBackIcon />}
        sx={{ 
          mb: 3,
          borderRadius: 999,
          px: 2,
          color: 'text.secondary',
          transition: 'all 0.2s ease',
          '&:hover': {
            color: 'primary.main',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            transform: 'translateX(-4px)'
          }
        }}
      >
        Volver al Paciente
      </Button>

      {/* Botón Tablas de Crecimiento (solo pacientes pediátricos) */}
      {esPediatrico && (
        <Button
          variant="outlined"
          color="primary"
          component={RouterLink}
          to={`/doctor/pacientes/${pacienteId}/crecimiento`}
          startIcon={<ChildCareIcon />}
          sx={{ mb: 4, ml: 2 }}
        >
          Tablas
        </Button>
      )}

      <Fade in={fadeIn} timeout={800}>
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, md: 3 }, 
            borderRadius: `${radius.lg}px`,
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.divider, 0.7),
            boxShadow: cardShadow,
            bgcolor: 'background.paper',
          }}
        >
          {/* Cabecera hero M3 */}
          <Box
            sx={(theme) => ({
              ...heroSurfaceSx(theme, 'primary'),
              p: { xs: 2.5, md: 3.5 },
              mb: 4,
              display: 'flex',
              alignItems: 'center',
            })}
          >
            <Box sx={(theme) => ({
              position: 'absolute', top: -70, right: -50, width: 220, height: 220,
              borderRadius: '50%', pointerEvents: 'none',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.14)} 0%, transparent 70%)`,
            })} />
            <Avatar 
              variant="rounded"
              sx={(theme) => ({ 
                bgcolor: 'primary.main', 
                width: 64, 
                height: 64,
                mr: 2.5,
                borderRadius: `${radius.md}px`,
                boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.4)}`,
                position: 'relative',
              })}
            >
              <MedicalServicesIcon sx={{ fontSize: 34 }} />
            </Avatar>
            <Box sx={{ position: 'relative' }}>
              <Typography variant="h4" fontWeight={800} sx={{ mb: 0.75, letterSpacing: -0.5 }}>
                {isEditMode ? 'Editar Consulta' : 'Nueva Consulta'}
              </Typography>
              {pacienteNombre && (
                <Chip
                  icon={<PersonOutlineIcon />}
                  label={pacienteNombre}
                  size="small"
                  sx={(theme) => ({
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main',
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.24),
                    '& .MuiChip-icon': { color: 'primary.main' },
                  })}
                />
              )}
            </Box>
          </Box>

          {selectedClinicId && user?.medico && (
            <Box sx={(theme) => ({
              mb: 3, p: 2, borderRadius: `${radius.sm}px`,
              display: 'flex', alignItems: 'center', gap: 1.5,
              bgcolor: alpha(theme.palette.info.main, 0.06),
              border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.18),
            })}>
              <Box sx={(theme) => sectionIconBoxSx(theme, 'info')}>
                <MedicalInformationIcon />
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, letterSpacing: 0.6 }}>
                  Médico que atiende
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  Dr(a). {user.medico?.usuario?.first_name || user.first_name || ''} {user.medico?.usuario?.last_name || user.last_name || ''}
                </Typography>
              </Box>
            </Box>
          )}
          
          {/* Date and Time field */}
          <Box mb={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <EventNoteIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={700}>
                Fecha de la Consulta
              </Typography>
            </Box>
            <TextField
              type="date"
              name="fecha_hora_consulta"
              value={formData.fecha_hora_consulta}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              // Mensaje de ayuda opcional
              helperText="La hora se establecerá automáticamente según tu zona horaria"
            />
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Formulario */}
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Signos Vitales */}
              <Accordion 
                elevation={0} 
                sx={(theme) => ({ 
                  borderRadius: `${radius.md}px !important`,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.7),
                  boxShadow: softShadow,
                  transition: 'box-shadow .25s ease',
                  '&:before': { display: 'none' },
                  '&:hover': { boxShadow: softShadowHover },
                  '&.Mui-expanded': { boxShadow: softShadowHover, margin: 0 },
                })}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />} 
                  sx={{ 
                    minHeight: 68,
                    px: 2.5,
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0',
                      alignItems: 'center',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={(theme) => sectionIconBoxSx(theme, 'error')}>
                      <MonitorHeartIcon />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: -0.2 }}>Signos Vitales</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Presión Arterial"
                    name="presion_arterial"
                    value={formData.presion_arterial || ''}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">mm/Hg</InputAdornment>,
                    }}
                    placeholder="120/80"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Pulso"
                    name="pulso"
                    value={formData.pulso || ''}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Frecuencia Cardiaca"
                    name="frecuencia_cardiaca"
                    value={formData.frecuencia_cardiaca || ''} 
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">lpm</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Frecuencia Respiratoria"
                    name="frecuencia_respiratoria"
                    value={formData.frecuencia_respiratoria || ''}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">rpm</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Perímetro Cefálico"
                    name="perimetro_cefalico"
                    value={formData.perimetro_cefalico || ''}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    helperText="Uso pediátrico (control de crecimiento OMS)"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Temperatura"
                    name="temperatura"
                    value={formData.temperatura || ''} 
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Select
                            value={unidadTemp}
                            onChange={(e) => setUnidadTemp(e.target.value)}
                            sx={{ minWidth: 68 }}
                            variant="standard"
                          >
                            <MenuItem value="°C">°C</MenuItem>
                            <MenuItem value="°F">°F</MenuItem>
                          </Select>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="SatO2"
                    name="saturacion_oxigeno"
                    value={formData.saturacion_oxigeno || ''}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>

                {/* Peso con selector de unidad */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Peso"
                    name="peso"
                    value={formData.peso || ''}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Select
                            value={unidadPeso}
                            onChange={(e) => setUnidadPeso(e.target.value)}
                            sx={{ minWidth: 60 }}
                            variant="standard"
                          >
                            <MenuItem value="Kg">Kg</MenuItem>
                            <MenuItem value="Lb">Lb</MenuItem>
                          </Select>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Altura con selector de unidad */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Altura"
                    name="altura"
                    value={formData.altura || ''}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Select
                            value={unidadAltura}
                            onChange={(e) => setUnidadAltura(e.target.value)}
                            sx={{ minWidth: 60 }}
                            variant="standard"
                          >
                            <MenuItem value="cm">cm</MenuItem>
                            <MenuItem value="m">m</MenuItem>
                            <MenuItem value="ft">ft</MenuItem>
                          </Select>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* IMC calculado */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="(IMC)"
                    name="imc"
                    value={formData.imc || ''}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                      endAdornment: <InputAdornment position="end">kg/m²</InputAdornment>,
                    }}
                  />
                </Grid>

                {/* Superficie corporal (Mosteller) calculada */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Superficie Corporal"
                    name="superficie_corporal"
                    value={formData.superficie_corporal || ''}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                      endAdornment: <InputAdornment position="end">m²</InputAdornment>,
                    }}
                    helperText="Se calcula automáticamente (Mosteller)"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
        </Accordion>
              
              {/* Información Clínica */}
              <Accordion 
                defaultExpanded 
                elevation={0} 
                sx={(theme) => ({ 
                  borderRadius: `${radius.md}px !important`,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.7),
                  boxShadow: softShadow,
                  transition: 'box-shadow .25s ease',
                  '&:before': { display: 'none' },
                  '&:hover': { boxShadow: softShadowHover },
                  '&.Mui-expanded': { boxShadow: softShadowHover, margin: 0 },
                })}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />} 
                  sx={{ 
                    minHeight: 68,
                    px: 2.5,
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0',
                      alignItems: 'center',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={(theme) => sectionIconBoxSx(theme, 'warning')}>
                      <HistoryEduIcon />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: -0.2 }}>Información Clínica</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  <Stack spacing={3}>
                    <TextField 
                      label="Antecedentes Personales Patológicos" 
                      name="antecedentes_patologicos" 
                      value={formData.antecedentes_patologicos || ''} 
                      onChange={handleInputChange} 
                      fullWidth 
                      multiline 
                      rows={4} 
                      variant="outlined" 
                    />
                    <TextField 
                      label="Antecedentes Inmuno-Alérgicos" 
                      name="antecedentes_alergicos" 
                      value={formData.antecedentes_alergicos || ''} 
                      onChange={handleInputChange} 
                      fullWidth 
                      multiline 
                      rows={4} 
                      variant="outlined" 
                    />
                    <TextField 
                      label="Antecedentes Toxicológicos" 
                      name="antecedentes_toxicologicos" 
                      value={formData.antecedentes_toxicologicos || ''} 
                      onChange={handleInputChange} 
                      fullWidth 
                      multiline 
                      rows={4} 
                      variant="outlined" 
                    />
                    <TextField 
                      label="Antecedentes Quirúrgicos" 
                      name="antecedentes_quirurgicos" 
                      value={formData.antecedentes_quirurgicos || ''} 
                      onChange={handleInputChange} 
                      fullWidth 
                      multiline 
                      rows={4} 
                      variant="outlined" 
                    />
                    
                    {/* Campo para antecedentes gineco-obstétricos (solo para pacientes femeninas) */}
                    {esPacienteFemenino && (
                      <Box
                        sx={(theme) => ({
                          p: 3,
                          borderRadius: `${radius.md}px`,
                          bgcolor: alpha(theme.palette.secondary.main, 0.06),
                          border: '1px solid',
                          borderColor: alpha(theme.palette.secondary.main, 0.20),
                        })}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={(theme) => sectionIconBoxSx(theme, 'secondary')}>
                              <PregnantWomanIcon />
                            </Box>
                            <Typography variant="subtitle1" color="secondary.main" fontWeight={700}>
                              Antecedentes Gineco-Obstétricos
                            </Typography>
                          </Box>
                          <Button size="small" variant="outlined" color="secondary" onClick={handleResetGinecoTemplate} sx={{ borderRadius: 999 }}>
                            Restablecer plantilla
                          </Button>
                        </Box>

                        {/* ---- Controles estructurados (dropdowns / fechas / numéricos) ---- */}
                        <Grid container spacing={2}>
                          {/* FUM / FPP */}
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="FUM (Última Menstruación)"
                              type="date"
                              value={ginecoFields.fum}
                              onChange={(e) => handleGinecoFieldChange('fum', e.target.value)}
                              fullWidth size="small"
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="FPP (Regla de Naegele)"
                              type="date"
                              value={ginecoFields.fpp}
                              onChange={(e) => handleGinecoFieldChange('fpp', e.target.value)}
                              fullWidth size="small"
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>

                          {/* G P C A HV HM */}
                          {[
                            { name: 'gestas', label: 'Gestas (G)' },
                            { name: 'partos', label: 'Partos (P)' },
                            { name: 'cesareas', label: 'Cesáreas (C)' },
                            { name: 'abortos', label: 'Abortos (A)' },
                            { name: 'hijos_vivos', label: 'Hijos Vivos (HV)' },
                            { name: 'hijos_muertos', label: 'Hijos Muertos (HM)' },
                          ].map((fld) => (
                            <Grid item xs={4} sm={2} key={fld.name}>
                              <TextField
                                label={fld.label}
                                type="number"
                                value={ginecoFields[fld.name]}
                                onChange={(e) => handleGinecoFieldChange(fld.name, e.target.value)}
                                fullWidth size="small"
                                inputProps={{ min: 0 }}
                              />
                            </Grid>
                          ))}

                          {/* Menarquia / Menopausia */}
                          <Grid item xs={6} sm={3}>
                            <TextField
                              label="Menarquia (edad)"
                              type="number"
                              value={ginecoFields.menarquia}
                              onChange={(e) => handleGinecoFieldChange('menarquia', e.target.value)}
                              fullWidth size="small"
                              inputProps={{ min: 0 }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              label="Menopausia (edad)"
                              value={ginecoFields.menopausia}
                              onChange={(e) => handleGinecoFieldChange('menopausia', e.target.value)}
                              fullWidth size="small"
                              placeholder="edad o 'No'"
                            />
                          </Grid>

                          {/* Ciclo días / regularidad */}
                          <Grid item xs={6} sm={3}>
                            <TextField
                              label="Ciclo (días)"
                              type="number"
                              value={ginecoFields.ciclo_dias}
                              onChange={(e) => handleGinecoFieldChange('ciclo_dias', e.target.value)}
                              fullWidth size="small"
                              inputProps={{ min: 0 }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Regularidad</InputLabel>
                              <Select
                                label="Regularidad"
                                value={ginecoFields.ciclo_regularidad}
                                onChange={(e) => handleGinecoFieldChange('ciclo_regularidad', e.target.value)}
                              >
                                {OPC_REGULARIDAD.map((o) => (
                                  <MenuItem key={o || 'none'} value={o}>{o || '—'}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Dismenorrea / Lactancia */}
                          <Grid item xs={6} sm={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Dismenorrea</InputLabel>
                              <Select
                                label="Dismenorrea"
                                value={ginecoFields.dismenorrea}
                                onChange={(e) => handleGinecoFieldChange('dismenorrea', e.target.value)}
                              >
                                {OPC_SI_NO.map((o) => (
                                  <MenuItem key={o || 'none'} value={o}>{o || '—'}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Lactancia actual</InputLabel>
                              <Select
                                label="Lactancia actual"
                                value={ginecoFields.lactancia}
                                onChange={(e) => handleGinecoFieldChange('lactancia', e.target.value)}
                              >
                                {OPC_SI_NO.map((o) => (
                                  <MenuItem key={o || 'none'} value={o}>{o || '—'}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Método anticonceptivo */}
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Método anticonceptivo actual</InputLabel>
                              <Select
                                label="Método anticonceptivo actual"
                                value={ginecoFields.anticonceptivo}
                                onChange={(e) => handleGinecoFieldChange('anticonceptivo', e.target.value)}
                              >
                                {OPC_ANTICONCEPTIVO.map((o) => (
                                  <MenuItem key={o || 'none'} value={o}>{o || '—'}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Citología: resultado + fecha */}
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Citología (Papanicolaou)</InputLabel>
                              <Select
                                label="Citología (Papanicolaou)"
                                value={ginecoFields.citologia_resultado}
                                onChange={(e) => handleGinecoFieldChange('citologia_resultado', e.target.value)}
                              >
                                {OPC_CITOLOGIA.map((o) => (
                                  <MenuItem key={o || 'none'} value={o}>{o || '—'}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Fecha citología"
                              type="date"
                              value={ginecoFields.citologia_fecha}
                              onChange={(e) => handleGinecoFieldChange('citologia_fecha', e.target.value)}
                              fullWidth size="small"
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>

                          {/* Mamografía: resultado + fecha */}
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Mamografía (BIRADS)</InputLabel>
                              <Select
                                label="Mamografía (BIRADS)"
                                value={ginecoFields.mamografia_resultado}
                                onChange={(e) => handleGinecoFieldChange('mamografia_resultado', e.target.value)}
                              >
                                {OPC_MAMOGRAFIA.map((o) => (
                                  <MenuItem key={o || 'none'} value={o}>{o || '—'}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Fecha mamografía"
                              type="date"
                              value={ginecoFields.mamografia_fecha}
                              onChange={(e) => handleGinecoFieldChange('mamografia_fecha', e.target.value)}
                              fullWidth size="small"
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>

                          {/* Embarazos previos / Antecedentes relevantes (texto) */}
                          <Grid item xs={12}>
                            <TextField
                              label="Embarazos previos y desenlaces"
                              value={ginecoFields.embarazos_previos}
                              onChange={(e) => handleGinecoFieldChange('embarazos_previos', e.target.value)}
                              fullWidth size="small" multiline rows={2}
                              placeholder="Ej: 2018 parto a término; 2021 cesárea..."
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Antecedentes obstétricos relevantes"
                              value={ginecoFields.obstetricos_relevantes}
                              onChange={(e) => handleGinecoFieldChange('obstetricos_relevantes', e.target.value)}
                              fullWidth size="small" multiline rows={2}
                              placeholder="Preeclampsia, diabetes gestacional, etc."
                            />
                          </Grid>
                        </Grid>

                        {/* ---- Texto libre (editable) que se guarda ---- */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, mb: 1 }}>
                          <InfoOutlinedIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            Los controles de arriba llenan este texto automáticamente. También puedes editarlo manualmente.
                          </Typography>
                        </Box>
                        <TextField
                          name="antecedentes_gineco_obstetricos"
                          value={formData.antecedentes_gineco_obstetricos || ''}
                          onChange={handleInputChange}
                          fullWidth
                          multiline
                          rows={8}
                          variant="outlined"
                          placeholder="FUM, FPP, G P C A HV, menarquia, menopausia, método anticonceptivo, citología, mamografía, etc."
                        />
                      </Box>
                    )}

                    {/* Antecedentes Psiquiátricos Personales y Familiares */}
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <PsychologyIcon sx={{ mr: 1, color: 'secondary.main' }} />
                        <Typography variant="subtitle1" color="secondary.main" fontWeight="medium">
                          Antecedentes Psiquiátricos Personales y Familiares
                        </Typography>
                      </Box>
                      <TextField
                        name="antecedentes_psiquiatricos"
                        value={formData.antecedentes_psiquiatricos || ''}
                        onChange={handleInputChange}
                        fullWidth
                        multiline
                        rows={5}
                        variant="outlined"
                        placeholder={
                          'Personales: depresión, ansiedad, esquizofrenia, trastorno bipolar, intentos de suicidio, hospitalizaciones psiquiátricas, medicación actual...\n' +
                          'Familiares: enfermedades psiquiátricas hereditarias, suicidios, trastornos del ánimo, adicciones...'
                        }
                      />
                    </Box>

                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          Anamnesis
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          position: 'relative',
                          border: '1px solid rgba(0, 0, 0, 0.23)',
                          borderRadius: 1,
                          overflow: 'hidden',
                          mt: 1
                        }}
                      >
                        <JoditEditor
                          ref={anamnesisEditorRef}
                          value={formData.anamnesis}
                          config={joditConfigAnamnesis}
                          onBlur={handleAnamnesisChange}
                          onChange={() => {}}
                        />
                        {/* Botón Flotante de IA */}
                        <Tooltip
                            title={
                              isListening
                                ? "🔴 Escuchando... Hablá con normalidad, la IA transcribirá y completará la HEA automáticamente. Hacé clic para detener."
                                : "🎤 Dictado con IA — Hacé clic para activar el micrófono y dictar la Historia de Enfermedad Actual. La IA procesará tu voz y completará el campo automáticamente."
                            }
                            arrow
                            TransitionComponent={Zoom}
                            placement="left"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: '0.78rem',
                                  maxWidth: 260,
                                  bgcolor: isListening ? 'error.dark' : 'primary.dark',
                                  '& .MuiTooltip-arrow': {
                                    color: isListening ? 'error.dark' : 'primary.dark',
                                  },
                                },
                              },
                            }}
                          >
                            <IconButton
                              onClick={handleToggleMic}
                              sx={{
                                position: 'absolute',
                                bottom: 12,
                                right: 12,
                                backgroundColor: isListening ? 'error.main' : 'primary.main',
                                color: 'white',
                                width: 52,
                                height: 52,
                                zIndex: 10,
                                boxShadow: isListening
                                  ? '0 0 0 3px rgba(211,47,47,0.3), 0 0 16px 4px rgba(211,47,47,0.5)'
                                  : '0 0 0 3px rgba(25,118,210,0.25), 0 0 14px 4px rgba(25,118,210,0.4)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  backgroundColor: isListening ? 'error.dark' : 'primary.dark',
                                  transform: 'scale(1.12)',
                                  boxShadow: isListening
                                    ? '0 0 0 4px rgba(211,47,47,0.4), 0 0 22px 8px rgba(211,47,47,0.6)'
                                    : '0 0 0 4px rgba(25,118,210,0.35), 0 0 22px 8px rgba(25,118,210,0.55)',
                                },
                                animation: isListening ? 'micPulse 1.5s infinite' : 'micGlow 2.5s ease-in-out infinite',
                                '@keyframes micPulse': {
                                  '0%'  : { boxShadow: '0 0 0 0 rgba(211,47,47,0.7), 0 0 10px 2px rgba(211,47,47,0.4)' },
                                  '70%' : { boxShadow: '0 0 0 12px rgba(211,47,47,0), 0 0 18px 6px rgba(211,47,47,0.2)' },
                                  '100%': { boxShadow: '0 0 0 0 rgba(211,47,47,0), 0 0 10px 2px rgba(211,47,47,0.4)' },
                                },
                                '@keyframes micGlow': {
                                  '0%'  : { boxShadow: '0 0 0 3px rgba(25,118,210,0.2), 0 0 10px 3px rgba(25,118,210,0.3)' },
                                  '50%' : { boxShadow: '0 0 0 3px rgba(25,118,210,0.4), 0 0 18px 7px rgba(25,118,210,0.5)' },
                                  '100%': { boxShadow: '0 0 0 3px rgba(25,118,210,0.2), 0 0 10px 3px rgba(25,118,210,0.3)' },
                                },
                              }}
                            >
                              <MicIcon sx={{ fontSize: 26 }} />
                            </IconButton>
                          </Tooltip>
                      </Box>
                    </Box>

                    {/* Examen físico con editor Jodit */}
                    <Box>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mb: 1 
                      }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Examen Físico
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                          Plantilla:
                        </Typography>
                      <Select
                         value={selectedTemplate}
                         onChange={handleTemplateChange}
                         size="small"
                         sx={{ minWidth: 150, mr: 1 }}
                       >
                         <MenuItem value="structured">Estructurada</MenuItem>
                         <MenuItem value="neurologica">Neurológica</MenuItem>
                         <MenuItem value="psiquiatrica">Psiquiátrica</MenuItem>
                         <MenuItem value="blank">En blanco</MenuItem>
                       </Select>
                       <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={handleResetTemplate}
                        >
                          Restablecer
                        </Button>
                     </Box>
                   </Box>
                   <Box 
                      sx={{ 
                        border: '1px solid rgba(0, 0, 0, 0.23)', 
                        borderRadius: 1,
                        overflow: 'hidden',
                        mt: 1
                      }}
                    >
                        <JoditEditor
                          ref={editorRef}
                          value={formData.examen_fisico}
                          config={joditConfigExamenFisico}
                          onBlur={handleEditorChange}
                          onChange={() => {}}
                        />
                      </Box>
                    </Box>
                    </Stack>
                    </AccordionDetails>
                    </Accordion>

              {/* Diagnósticos */}
              <Accordion 
                defaultExpanded 
                elevation={0} 
                sx={(theme) => ({ 
                  borderRadius: `${radius.md}px !important`,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.7),
                  boxShadow: softShadow,
                  transition: 'box-shadow .25s ease',
                  '&:before': { display: 'none' },
                  '&:hover': { boxShadow: softShadowHover },
                  '&.Mui-expanded': { boxShadow: softShadowHover, margin: 0 },
                })}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />} 
                  sx={{ 
                    minHeight: 68,
                    px: 2.5,
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0',
                      alignItems: 'center',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={(theme) => sectionIconBoxSx(theme, 'error')}>
                      <HealingIcon />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: -0.2 }}>Diagnósticos</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  <Stack spacing={3}>
                    <Autocomplete
                      freeSolo 
                      options={icdOptions}
                      getOptionLabel={option => (typeof option === 'string' ? option : stripHtmlTags(option.title) || '')}
                      filterOptions={(x) => x} 
                      loading={loadingIcd}
                      inputValue={diagnosticoPrincipalInputValue}
                      onInputChange={(event, newInputValue) => {
                        setDiagnosticoPrincipalInputValue(newInputValue);
                        searchICD(newInputValue);
                      }}
                      onChange={handleAutocompleteChange}
                      renderOption={(props, option) => (
                        <li {...props}>
                          {stripHtmlTags(option.title)}
                        </li>
                      )}
                      renderInput={params => (
                        <TextField 
                          {...params} 
                          label="Diagnóstico Principal (CIE-11)" 
                          fullWidth 
                          required 
                          helperText="Escribe al menos 3 caracteres para buscar" 
                          InputProps={{
                            ...params.InputProps, 
                            endAdornment: (
                              <>
                                {loadingIcd && <CircularProgress color="inherit" size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }} 
                        />
                      )}
                    />
                    {/* Diagnóstico Secundario */}
                    <Autocomplete
                      freeSolo 
                      options={icdOptions}
                      getOptionLabel={option => (typeof option === 'string' ? option : stripHtmlTags(option.title) || '')}
                      filterOptions={(x) => x} 
                      loading={loadingIcd}
                      inputValue={diagnosticoSecundarioInputValue}
                      onInputChange={(event, newInputValue) => {
                        setDiagnosticoSecundarioInputValue(newInputValue);
                        searchICD(newInputValue);
                      }}
                      onChange={(event, newValue) => handleDiagnosticoChange(
                        event, newValue, setDiagnosticoSecundarioInputValue, 'diagnostico_secundario'
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id || option.title}>
                          {stripHtmlTags(option.title)}
                        </li>
                      )}
                      renderInput={params => (
                        <TextField 
                          {...params} 
                          label="Diagnóstico Secundario (CIE-11)" 
                          fullWidth 
                          helperText="Escribe al menos 3 caracteres para buscar" 
                          InputProps={{
                            ...params.InputProps, 
                            endAdornment: (
                              <>
                                {loadingIcd && <CircularProgress color="inherit" size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }} 
                        />
                      )}
                    />
                    {/* Diagnóstico Terciario */}
                    <Autocomplete
                      freeSolo 
                      options={icdOptions}
                      getOptionLabel={option => (typeof option === 'string' ? option : stripHtmlTags(option.title) || '')}
                      filterOptions={(x) => x} 
                      loading={loadingIcd}
                      inputValue={diagnosticoTerciarioInputValue}
                      onInputChange={(event, newInputValue) => {
                        setDiagnosticoTerciarioInputValue(newInputValue);
                        searchICD(newInputValue);
                      }}
                      onChange={(event, newValue) => handleDiagnosticoChange(
                        event, newValue, setDiagnosticoTerciarioInputValue, 'diagnostico_terciario'
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id || option.title}>
                          {stripHtmlTags(option.title)}
                        </li>
                      )}
                      renderInput={params => (
                        <TextField 
                          {...params} 
                          label="Diagnóstico Terciario (CIE-11)" 
                          fullWidth 
                          helperText="Escribe al menos 3 caracteres para buscar" 
                          InputProps={{
                            ...params.InputProps, 
                            endAdornment: (
                              <>
                                {loadingIcd && <CircularProgress color="inherit" size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }} 
                        />
                      )}
                    />

                    {/* Diagnóstico Cuaternario */}
                    <Autocomplete
                      freeSolo 
                      options={icdOptions}
                      getOptionLabel={option => (typeof option === 'string' ? option : stripHtmlTags(option.title) || '')}
                      filterOptions={(x) => x} 
                      loading={loadingIcd}
                      inputValue={diagnosticoCuaternarioInputValue}
                      onInputChange={(event, newInputValue) => {
                        setDiagnosticoCuaternarioInputValue(newInputValue);
                        searchICD(newInputValue);
                      }}
                      onChange={(event, newValue) => handleDiagnosticoChange(
                        event, newValue, setDiagnosticoCuaternarioInputValue, 'diagnostico_cuaternario'
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id || option.title}>
                          {stripHtmlTags(option.title)}
                        </li>
                      )}
                      renderInput={params => (
                        <TextField 
                          {...params} 
                          label="Diagnóstico Cuaternario (CIE-11)" 
                          fullWidth 
                          helperText="Escribe al menos 3 caracteres para buscar" 
                          InputProps={{
                            ...params.InputProps, 
                            endAdornment: (
                              <>
                                {loadingIcd && <CircularProgress color="inherit" size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }} 
                        />
                      )}
                    />
                    </Stack>
                    </AccordionDetails>
                    </Accordion>


              {/* Exámenes Laboratoriales */}
              <Accordion 
                elevation={0} 
                sx={(theme) => ({ 
                  borderRadius: `${radius.md}px !important`,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.7),
                  boxShadow: softShadow,
                  transition: 'box-shadow .25s ease',
                  '&:before': { display: 'none' },
                  '&:hover': { boxShadow: softShadowHover },
                  '&.Mui-expanded': { boxShadow: softShadowHover, margin: 0 },
                })}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />} 
                  sx={{ 
                    minHeight: 68,
                    px: 2.5,
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0',
                      alignItems: 'center',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={(theme) => sectionIconBoxSx(theme, 'success')}>
                      <BiotechIcon />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: -0.2 }}>Exámenes Laboratoriales</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  <Autocomplete
                    multiple 
                    freeSolo
                    options={catalogoExamenes}
                    groupBy={(option) => option.categoria}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option.nombre}
                    value={formData.examenes_laboratoriales ? formData.examenes_laboratoriales.split('\n').filter(Boolean) : []}
                    onChange={(event, newValue) => {
                      const valueToSave = newValue.map(item => typeof item === 'string' ? item : item.nombre).join('\n');
                      setFormData(prev => ({ ...prev, examenes_laboratoriales: valueToSave }));
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...rest } = getTagProps({ index });
                        return (
                        <Chip
                          key={key}
                          {...rest}
                          variant="filled"
                          label={option}
                          sx={{ m: 0.5 }}
                        />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Buscar en tu catálogo o añadir nuevo" 
                        helperText="Selecciona de tu lista o escribe y presiona Enter" 
                      />
                    )}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Exámenes de Imagen */}
              <Accordion 
                elevation={0} 
                sx={(theme) => ({ 
                  borderRadius: `${radius.md}px !important`,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.7),
                  boxShadow: softShadow,
                  transition: 'box-shadow .25s ease',
                  '&:before': { display: 'none' },
                  '&:hover': { boxShadow: softShadowHover },
                  '&.Mui-expanded': { boxShadow: softShadowHover, margin: 0 },
                })}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />} 
                  sx={{ 
                    minHeight: 68,
                    px: 2.5,
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0',
                      alignItems: 'center',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={(theme) => sectionIconBoxSx(theme, 'info')}>
                      <ImageSearchIcon />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: -0.2 }}>Exámenes de Imagen</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  <TextField 
                    label="Rayos X, Tomografías, Resonancias, etc." 
                    name="examenes_imagen" 
                    value={formData.examenes_imagen} 
                    onChange={handleInputChange} 
                    multiline 
                    rows={6} 
                    fullWidth 
                  />
                </AccordionDetails>
              </Accordion>
              
              {/* Plan de Tratamiento */}
              <Accordion 
                elevation={0} 
                sx={(theme) => ({ 
                  borderRadius: `${radius.md}px !important`,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.7),
                  boxShadow: softShadow,
                  transition: 'box-shadow .25s ease',
                  '&:before': { display: 'none' },
                  '&:hover': { boxShadow: softShadowHover },
                  '&.Mui-expanded': { boxShadow: softShadowHover, margin: 0 },
                })}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />} 
                  sx={{ 
                    minHeight: 68,
                    px: 2.5,
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0',
                      alignItems: 'center',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={(theme) => sectionIconBoxSx(theme, 'primary')}>
                      <HealingIcon />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: -0.2 }}>Plan de Tratamiento</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  <TextField 
                    label="Plan de Tratamiento" 
                    name="plan_tratamiento" 
                    value={formData.plan_tratamiento} 
                    onChange={handleInputChange} 
                    fullWidth 
                    multiline 
                    rows={6} 
                    variant="outlined" 
                  />
                </AccordionDetails>
                {/* Órdenes médicas para Enfermería (SOLO modo clínica) */}
                {selectedClinicId && (
                <Box sx={{ mt: 3, p: 2, borderRadius: 2, border: '1px dashed', borderColor: 'divider', bgcolor: 'background.subtle' }}>
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                    Órdenes médicas para Enfermería
                  </Typography>
                  <TextField
                    label="Órdenes de Enfermería"
                    value={ordenesEnfermeria}
                    onChange={(e) => setOrdenesEnfermeria(e.target.value)}
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    placeholder="Indicaciones para Enfermería (p.ej. control de signos cada 4h, administrar medicación, curaciones, etc.)"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Estas órdenes se anexarán al Plan de Tratamiento al guardar.
                  </Typography>
                </Box>
              )}
              </Accordion>

              {/* Receta Médica */}
              <Accordion 
                elevation={0} 
                sx={(theme) => ({ 
                  borderRadius: `${radius.md}px !important`,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.7),
                  boxShadow: softShadow,
                  transition: 'box-shadow .25s ease',
                  '&:before': { display: 'none' },
                  '&:hover': { boxShadow: softShadowHover },
                  '&.Mui-expanded': { boxShadow: softShadowHover, margin: 0 },
                })}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />} 
                  sx={{ 
                    minHeight: 68,
                    px: 2.5,
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0',
                      alignItems: 'center',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={(theme) => sectionIconBoxSx(theme, 'secondary')}>
                      <MedicationIcon />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: -0.2 }}>Receta Médica</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>

                  {/* ── Panel: Receta de consulta anterior ── */}
                  {!isEditMode && ultimaConsulta && (
                    <Box sx={{ mb: 3 }}>
                      {/* Chip / trigger */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: panelRecetaAnterior ? 1.5 : 0 }}>
                        <Chip
                          icon={<HistoryIcon />}
                          label={`Ver receta anterior — ${ultimaConsulta.fecha}`}
                          color="secondary"
                          variant={panelRecetaAnterior ? 'filled' : 'outlined'}
                          onClick={() => {
                            setPanelRecetaAnterior(p => !p);
                            setSelectedMedsAnteriores({});
                          }}
                          onDelete={panelRecetaAnterior ? () => setPanelRecetaAnterior(false) : undefined}
                          deleteIcon={<ExpandLessIcon />}
                          sx={{ fontWeight: 500, cursor: 'pointer' }}
                        />
                        {loadingUltimaMeds && <CircularProgress size={16} />}
                      </Box>

                      {/* Panel colapsable */}
                      {panelRecetaAnterior && (
                        <Paper
                          variant="outlined"
                          sx={{
                            borderRadius: 3,
                            overflow: 'hidden',
                            border: '1.5px solid',
                            borderColor: 'secondary.light',
                            bgcolor: 'background.paper',
                          }}
                        >
                          {/* Encabezado del panel */}
                          <Box
                            sx={{
                              px: 2.5, py: 1.5,
                              bgcolor: 'secondary.main',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <HistoryIcon sx={{ color: 'secondary.contrastText', fontSize: 20 }} />
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'secondary.contrastText' }}>
                                Receta anterior — {ultimaConsulta.fecha}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                size="small"
                                label={ultimaConsulta.medicamentos.length + ' med.'}
                                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'secondary.contrastText', fontWeight: 600 }}
                              />
                            </Box>
                          </Box>

                          {/* Diagnóstico de la consulta anterior */}
                          {ultimaConsulta.diagnostico && (
                            <Box sx={{ px: 2.5, py: 1, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                DIAGNÓSTICO ANTERIOR:&nbsp;
                              </Typography>
                              <Typography variant="caption" color="text.primary">
                                {ultimaConsulta.diagnostico}
                                {ultimaConsulta.diagnostico_secundario ? ` · ${ultimaConsulta.diagnostico_secundario}` : ''}
                              </Typography>
                            </Box>
                          )}

                          {/* Lista de medicamentos con checkboxes */}
                          <List disablePadding dense>
                            {ultimaConsulta.medicamentos.map((med, idx) => (
                              <ListItem
                                key={idx}
                                divider={idx < ultimaConsulta.medicamentos.length - 1}
                                sx={{
                                  px: 2.5, py: 1,
                                  bgcolor: selectedMedsAnteriores[idx] ? 'action.selected' : 'transparent',
                                  transition: 'background-color 0.15s',
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={!!selectedMedsAnteriores[idx]}
                                  onChange={(e) =>
                                    setSelectedMedsAnteriores(prev => ({ ...prev, [idx]: e.target.checked }))
                                  }
                                  sx={{ mr: 1, p: 0.5 }}
                                />
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                      <Typography variant="body2" fontWeight="600">
                                        {med.nombre}
                                        {med.nombre_comercial ? ` (${med.nombre_comercial})` : ''}
                                      </Typography>
                                      {med.dosis && (
                                        <Chip size="small" label={med.dosis} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                      )}
                                      {med.receta_tag && med.receta_tag !== 'general' && (
                                        <Chip size="small" label={med.receta_tag} color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                                      )}
                                    </Box>
                                  }
                                  secondary={
                                    <Typography variant="caption" color="text.secondary">
                                      {[med.frecuencia, med.duracion].filter(Boolean).join(' · ')}
                                      {med.notas ? ` — ${med.notas}` : ''}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>

                          {/* Acciones */}
                          <Box
                            sx={{
                              px: 2.5, py: 1.5,
                              borderTop: '1px solid',
                              borderColor: 'divider',
                              display: 'flex',
                              gap: 1,
                              justifyContent: 'flex-end',
                              flexWrap: 'wrap',
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                const allSelected = {};
                                ultimaConsulta.medicamentos.forEach((_, i) => { allSelected[i] = true; });
                                setSelectedMedsAnteriores(allSelected);
                              }}
                            >
                              Seleccionar todos
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="secondary"
                              startIcon={<ContentCopyIcon />}
                              disabled={Object.values(selectedMedsAnteriores).every(v => !v)}
                              onClick={() => {
                                const seleccionados = ultimaConsulta.medicamentos.filter((_, i) => selectedMedsAnteriores[i]);
                                if (seleccionados.length === 0) return;
                                // Evitar duplicados exactos por nombre
                                const nombresActuales = new Set(medicamentos.map(m => m.nombre?.toLowerCase().trim()));
                                const nuevos = seleccionados.filter(
                                  m => !nombresActuales.has(m.nombre?.toLowerCase().trim())
                                );
                                const duplicados = seleccionados.length - nuevos.length;
                                if (nuevos.length > 0) {
                                  setMedicamentos(prev => [...prev, ...nuevos]);
                                  toast.success(
                                    `${nuevos.length} medicamento${nuevos.length > 1 ? 's' : ''} copiado${nuevos.length > 1 ? 's' : ''} a la receta`
                                    + (duplicados > 0 ? ` (${duplicados} ya existía${duplicados > 1 ? 'n' : ''})` : '')
                                  );
                                } else {
                                  toast.info('Todos los medicamentos seleccionados ya están en la receta');
                                }
                                setSelectedMedsAnteriores({});
                                setPanelRecetaAnterior(false);
                              }}
                            >
                              Copiar seleccionados
                            </Button>
                          </Box>
                        </Paper>
                      )}
                    </Box>
                  )}
                  {/* ── Fin Panel Receta Anterior ── */}

                  {medicamentos.length > 0 ? (
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        borderRadius: 3, 
                        overflow: 'hidden',
                        mb: 2
                      }}
                    >
                      <List disablePadding>
                        {medicamentos.map((med, index) => (
                          <ListItem 
                            key={index} 
                            divider={index < medicamentos.length - 1}
                            secondaryAction={
                              <Box>
                                <Tooltip title="Editar medicamento">
                                  <IconButton 
                                    edge="end" 
                                    onClick={() => handleOpenMedicamentoModal(med, index)}
                                    sx={{ mr: 1 }}
                                  >
                                    <EditIcon color="primary" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar medicamento">
                                  <IconButton 
                                    edge="end" 
                                    onClick={() => handleDeleteMedicamento(index)}
                                  >
                                    <DeleteIcon color="error" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            }
                            sx={{ py: 1.5 }}
                          >
                            {/* Primary content */}
                            <Box sx={{ pr: 2, flex: 1 }}>
                              {/* Medication name and details */}
                              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                                <Typography fontWeight="medium" component="span">
                                  {med.nombre}
                                </Typography>
                                {med.nombre_comercial && (
                                  <Chip 
                                    label={med.nombre_comercial} 
                                    size="small" 
                                    variant="filled" 
                                    color="primary"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                                <Chip 
                                  label={med.dosis} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ ml: 1 }}
                                />
                                {med.receta_tag && (
                                  <Chip
                                    label={med.receta_tag === 'general' ? 'Normal' : med.receta_tag}
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                                {med.source && (
                                  <Chip label={med.source} size="small" variant="outlined" sx={{ ml: 1 }} />
                                )}  
                              </Box>
                              
                              {/* Medication instructions */}
                              <Typography variant="body2" color="text.secondary" component="div">
                                Indicación: {med.frecuencia}, por {med.duracion}
                              </Typography>
                              
                              {/* Notes (if any) */}
                              {med.notas && (
                                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }} component="div">
                                  <InfoOutlinedIcon fontSize="small" sx={{ mr: 0.5, fontSize: '1rem' }} />
                                  <Typography variant="body2" color="text.secondary" component="span">
                                    {med.notas}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  ) : (
                    <Box 
                      sx={(theme) => ({ 
                        p: 4, 
                        textAlign: 'center', 
                        bgcolor: alpha(theme.palette.secondary.main, 0.04),
                        borderRadius: `${radius.md}px`,
                        border: '1px dashed',
                        borderColor: alpha(theme.palette.secondary.main, 0.30),
                        mb: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                      })}
                    >
                      <Box sx={(theme) => ({ ...sectionIconBoxSx(theme, 'secondary'), width: 52, height: 52, mr: 0 })}>
                        <MedicationIcon />
                      </Box>
                      <Typography color="text.secondary" fontWeight={500}>
                        No hay medicamentos agregados a la receta
                      </Typography>
                    </Box>
                  )}

                  <Button
                    variant="tonal"
                    color="secondary"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => handleOpenMedicamentoModal()}
                    sx={{ mt: 1, borderRadius: 999, px: 3 }}
                  >
                    Añadir Medicamento
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<SendIcon />}
                    onClick={handleSendRecipeEmail}
                    sx={{ mt: 1, ml: 2 }}
                    disabled={!isEditMode || sendingRecipe}
                  >
                    {sendingRecipe ? 'Enviando...' : 'Enviar receta por email'}
                  </Button>
                  {recetaTags.length > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        Enviar solo la receta:
                      </Typography>
                      <Select
                        size="small"
                        value={selectedTagToEmail}
                        onChange={(e) => setSelectedTagToEmail(e.target.value)}
                        sx={{ minWidth: 180 }}
                      >
                        {recetaTags.map(t => (
                          <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                      </Select>
                      <Button
                        variant="outlined"
                        onClick={async () => {
                          if (!isEditMode) {
                            toast.info('Primero guarda la consulta para enviar receta');
                            return;
                          }
                          if (selectedTagToEmail === '') {
                            toast.info('Selecciona "Normal" o "Controlado"');
                            return;
                          }  
                          
                          const email = window.prompt('Correo destino (deja vacío para usar el del paciente):', '');
                          try {
                            await axiosInstance.post(`/consultas/${consultaId}/enviar-receta/`, {
                              receta_tag: selectedTagToEmail,
                              ...(email ? { email } : {})
                            });
                            toast.success(`Receta "${selectedTagToEmail}" enviada`);
                          } catch (e) {
                            const msg = e?.response?.data?.error || 'No se pudo enviar la receta';
                            toast.error(msg);
                          }
                        }}
                      >
                        Enviar receta seleccionada
                      </Button>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Botones de acción */}
              <Box mt={4} display="flex" justifyContent="flex-end" sx={{ gap: 2 }}>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  size="large" 
                  disabled={saving} 
                  onClick={() => navigate(isEditMode ? `/consultas/${consultaId}` : `/pacientes/${pacienteId}`)}
                  startIcon={<CancelIcon />}
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  Cancelar
                </Button>

                {selectedClinicId && (
                  <Button
                    type="button"
                    variant="tonal"
                    color="info"
                    size="large"
                    onClick={() => setDeriveOpen(true)}
                    sx={{
                      borderRadius: 999,
                      px: 3,
                      transition: 'all 0.2s ease',
                      '&:hover': { transform: 'translateY(-2px)' },
                    }}
                  >
                    Derivar paciente
                  </Button>
                )}

                {!selectedClinicId && (
                  <Button
                    type="button"
                    variant="tonal"
                    color="secondary"
                    size="large"
                    startIcon={<CompareArrowsIcon />}
                    onClick={() => setInterconsultaOpen(true)}
                    sx={{
                      borderRadius: 999,
                      px: 3,
                      transition: 'all 0.2s ease',
                      '&:hover': { transform: 'translateY(-2px)' },
                    }}
                  >
                    Interconsulta
                  </Button>
                )}

                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  size="large" 
                  disabled={saving}
                  startIcon={saving ? null : <SaveIcon />}
                  sx={(theme) => ({
                    borderRadius: 999,
                    px: 4,
                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 10px 22px ${alpha(theme.palette.primary.main, 0.5)}`,
                    }
                  })}
                >
                  {saving ? <CircularProgress size={24} color="inherit" /> : (isEditMode ? 'Actualizar Consulta' : 'Guardar Consulta')}
                </Button>
              </Box>
            </Stack>
          </form>
        </Paper>
      </Fade>

              {/* Diálogo para añadir/editar medicamento */}
              <Dialog 
                open={openMedicamentoModal} 
                onClose={handleCloseMedicamentoModal} 
                fullWidth 
                maxWidth="sm"
                PaperProps={{
                  elevation: 3,
                  sx: {
                    borderRadius: 4,
                    overflow: 'hidden'
                  }
                }}
              >
                <DialogTitle sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'primary.contrastText',
                  py: 2
                }}>
                  {isEditMedicamentoMode ? 'Editar Medicamento' : 'Añadir Medicamento'}
                </DialogTitle>

                <DialogContent sx={{ p: 3, mt: 1 }}>
                  <Stack spacing={3} sx={{ pt: 1 }}>
                    <Autocomplete
                      freeSolo
                      options={[...catalogoMedicamentos, ...cimaOptions]}
                      groupBy={(option) => option.source || 'Mi Catálogo'}
                      getOptionLabel={(option) => (typeof option === 'string' ? option : (option.nombre || ''))}
                      isOptionEqualToValue={(opt, val) => {
                        if (typeof opt === 'string' || typeof val === 'string') return opt === val;
                        return (
                          (opt?.nombre || '') === (val?.nombre || '') &&
                          (opt?.dosis || '') === (val?.dosis || '') &&
                          (opt?.source || '') === (val?.source || '')
                        );
                      }}
                      renderOption={(props, option) => {
                        const { key, ...rest } = props;
                        const customKey = buildOptionKey(option);
                        return (
                          <li key={customKey} {...rest}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                              <span>{option.nombre}</span>
                              {option.source && (
                                <Chip
                                  label={option.source}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </div>
                          </li>
                        );
                      }}
                      loading={loadingCima}
                      filterOptions={(options, state) => {
                        const term = (state.inputValue || '').toLowerCase();
                        const localFiltered = options.filter(
                          (opt) => !opt.source && (opt.nombre || '').toLowerCase().includes(term)
                        );
                        const externalResults = options.filter(
                          (opt) => !!opt.source && (opt.nombre || '').toLowerCase().includes(term)
                        );
                        return [...localFiltered, ...externalResults];
                      }}
                      onInputChange={(event, newInputValue) => {
                        searchDrugs(newInputValue);
                        setCurrentMedicamento((prev) => ({ ...prev, nombre: newInputValue }));
                      }}
                      onChange={(event, newValue) => {
                        if (!newValue) return;
                        if (typeof newValue === 'string') {
                          setCurrentMedicamento((prev) => ({
                            ...prev,
                            nombre: newValue,
                            source: null,
                          }));
                          return;
                        }
                        setCurrentMedicamento((prev) => ({
                          ...prev,
                          nombre: newValue.nombre,
                          dosis: newValue.dosis_sugerida || newValue.dosis || '',
                          notas: newValue.notas_predeterminadas || '',
                          source: newValue.source || null,
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Buscar en Catálogo FDA o en Base Externa"
                          required
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingCima ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />

                    <TextField 
                      label="Nombre Comercial (Opcional)" 
                      name="nombre_comercial" 
                      value={currentMedicamento?.nombre_comercial || ''} 
                      onChange={handleMedicamentoInputChange} 
                      fullWidth 
                    />
                    <TextField 
                      label="Dosis (ej: 500 mg)" 
                      name="dosis" 
                      value={currentMedicamento?.dosis || ''} 
                      onChange={handleMedicamentoInputChange} 
                      fullWidth 
                    />
                    <TextField 
                      label="Frecuencia (ej: cada 8 horas)" 
                      name="frecuencia" 
                      value={currentMedicamento?.frecuencia || ''} 
                      onChange={handleMedicamentoInputChange} 
                      fullWidth 
                    />
                    <TextField 
                      label="Duración (ej: por 7 días)" 
                      name="duracion" 
                      value={currentMedicamento?.duracion || ''} 
                      onChange={handleMedicamentoInputChange} 
                      fullWidth 
                    />
                    <TextField 
                      label="Notas Adicionales" 
                      name="notas" 
                      value={currentMedicamento?.notas || ''} 
                      onChange={handleMedicamentoInputChange} 
                      fullWidth 
                      multiline 
                      rows={4} 
                    />
                    <FormControl fullWidth>
                      <InputLabel id="receta-tag-label" shrink>
                        Etiqueta de receta
                      </InputLabel>  
                      <Select
                        labelId="receta-tag-label"
                        label="Etiqueta de receta"
                        name="receta_tag"
                        value={currentMedicamento?.receta_tag ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCurrentMedicamento(prev => ({ ...prev, receta_tag: value }));
                        }}
                        // Permite dejar en blanco (sin etiqueta)
                        displayEmpty
                        renderValue={(value) => {
                          if (!value) {
                          return <span style={{ color: 'rgba(0,0,0,0.6)' }}>Sin etiqueta</span>
                        }
                        return value === 'general' ? 'Normal' : (value === 'controlados' ? 'Controlado' : value) 
                      }}  
                      >
                        <MenuItem value="">
                          <em>Sin etiqueta</em>
                        </MenuItem>
                        <MenuItem value="general">Normal</MenuItem>
                        <MenuItem value="controlados">Controlado</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.subtle' }}>
                  <Button 
                    onClick={handleCloseMedicamentoModal} 
                    variant="outlined"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleSaveMedicamento} 
                    variant="contained"
                    sx={{ ml: 1 }}
                  >
                    Guardar
                  </Button>
                </DialogActions>
              </Dialog>

              {/* === Dialog de Derivación (SEPARADO, NO ANIDADO) === */}
              <Dialog
                open={deriveOpen}
                onClose={() => setDeriveOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                  elevation: 3,
                  sx: { borderRadius: 4, overflow: 'hidden' }
                }}
              >
                <DialogTitle sx={{ bgcolor: 'info.main', color: 'info.contrastText', py: 2 }}>
                  Derivar paciente
                </DialogTitle>

                <DialogContent sx={{ p: 3, mt: 1 }}>
                  <Stack spacing={3} sx={{ pt: 1 }}>
                    <Autocomplete
                      options={clinicDoctors || []}
                      getOptionLabel={(opt) => opt?.name || 'Médico'}
                      isOptionEqualToValue={(a, b) => a?.userId === b?.userId}
                      value={deriveDoctor}
                      onChange={(e, val) => setDeriveDoctor(val)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Seleccionar médico de la clínica"
                          placeholder="Buscar médico"
                          helperText="Sólo se listan médicos de la clínica activa"
                          required
                        />
                      )}
                    />

                    <TextField
                      label="Motivo de interconsulta"
                      value={deriveReason}
                      onChange={(e) => setDeriveReason(e.target.value)}
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Describe brevemente la/s causa/s de la derivación"
                    />
                  </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.subtle' }}>
                  <Button onClick={() => setDeriveOpen(false)} variant="outlined">Cancelar</Button>
                  <Button
                    onClick={() => {
                      if (!deriveDoctor || !deriveDoctor.name) {
                        toast.error('Selecciona el médico al que derivar');
                        return;
                      }
                      const stamp = new Date().toLocaleString();
                      const tag = `[Derivación] Enviado a Dr(a). ${deriveDoctor.name} — Motivo: ${deriveReason || 'No especificado'} — ${stamp}`;
                      setFormData(prev => ({
                        ...prev,
                        plan_tratamiento: (prev.plan_tratamiento ? (prev.plan_tratamiento + '\n') : '') + tag
                      }));
                      setDeriveOpen(false);
                      setDeriveDoctor(null);
                      setDeriveReason('');
                      toast.success('Derivación registrada en la consulta');
                    }}
                    variant="contained"
                    sx={{ ml: 1 }}
                  >
                    Confirmar
                  </Button>
                </DialogActions>
              </Dialog>

              {/* === Dialog de Interconsulta (modo doctor solo) === */}
              <Dialog
                open={interconsultaOpen}
                onClose={() => setInterconsultaOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                  elevation: 3,
                  sx: { borderRadius: 4, overflow: 'hidden' }
                }}
              >
                <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText', py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CompareArrowsIcon />
                  Referencia médica / Interconsulta
                </DialogTitle>

                <DialogContent sx={{ p: 3, mt: 1 }}>
                  <Stack spacing={3} sx={{ pt: 1 }}>
                    <TextField
                      label="Nombre del médico referido"
                      placeholder="Ej. Dr. Juan Pérez"
                      value={interconsultaData.nombreDoctor}
                      onChange={(e) => setInterconsultaData(prev => ({ ...prev, nombreDoctor: e.target.value }))}
                      fullWidth
                      required
                    />
                    <TextField
                      label="Especialidad"
                      placeholder="Ej. Cardiología, Neurología..."
                      value={interconsultaData.especialidad}
                      onChange={(e) => setInterconsultaData(prev => ({ ...prev, especialidad: e.target.value }))}
                      fullWidth
                      required
                    />

                    {/* Historia de la Enfermedad Actual / Motivo de Consulta */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Historia de la Enfermedad Actual / Motivo de Consulta
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => {
                            const strippedAnamnesis = (formData?.anamnesis || '').replace(/<[^>]*>/g, '').trim();
                            if (!strippedAnamnesis) {
                              toast.info('No hay anamnesis para copiar');
                              return;
                            }
                            setInterconsultaData(prev => ({
                              ...prev,
                              historiaEnfermedad: prev.historiaEnfermedad
                                ? prev.historiaEnfermedad + '\n\n' + strippedAnamnesis
                                : strippedAnamnesis
                            }));
                            toast.success('Anamnesis agregada');
                          }}
                          disabled={!formData?.anamnesis}
                        >
                          Copiar Anamnesis
                        </Button>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={interconsultaData.historiaEnfermedad}
                        onChange={(e) => setInterconsultaData(prev => ({ ...prev, historiaEnfermedad: e.target.value }))}
                        placeholder="Describa la historia de la enfermedad actual o copie la anamnesis..."
                      />
                    </Box>

                    

                    {/* Tratamiento actual */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Tratamiento actual
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => {
                            if (!medicamentos || medicamentos.length === 0) {
                              toast.info('No hay medicamentos en la receta para copiar');
                              return;
                            }
                            const medicamentosText = medicamentos.map(med => {
                              let text = `- ${med.nombre || ''}`;
                              if (med.nombre_comercial) text += ` (${med.nombre_comercial})`;
                              if (med.dosis) text += ` - ${med.dosis}`;
                              const freq = med.frecuencia || '';
                              const dur = med.duracion || '';
                              if (freq || dur) {
                                text += `\n  Indicaci\u00f3n: ${freq}${freq && dur ? ', por ' : ''}${dur}`;
                              }
                              if (med.notas) text += `\n  Notas: ${med.notas}`;
                              return text;
                            }).join('\n\n');

                            setInterconsultaData(prev => ({
                              ...prev,
                              tratamiento: prev.tratamiento
                                ? prev.tratamiento + '\n\n' + medicamentosText
                                : medicamentosText
                            }));
                            toast.success('Receta m\u00e9dica agregada');
                          }}
                          disabled={!medicamentos || medicamentos.length === 0}
                        >
                          Copiar Receta
                        </Button>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={interconsultaData.tratamiento}
                        onChange={(e) => setInterconsultaData(prev => ({ ...prev, tratamiento: e.target.value }))}
                        placeholder="Tratamiento actual del paciente o copie la receta..."
                      />
                    </Box>

                    {/* Envío por correo electrónico */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Enviar por correo electrónico (opcional)
                      </Typography>
                      <TextField
                        fullWidth
                        type="email"
                        label="Correo del destinatario"
                        placeholder="doctor@ejemplo.com"
                        value={interconsultaData.email}
                        onChange={(e) => setInterconsultaData(prev => ({ ...prev, email: e.target.value }))}
                        inputProps={{ inputMode: 'email' }}
                        helperText="Si se ingresa un correo, podrás enviar el PDF directamente al destinatario."
                      />
                    </Box>
                  </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.subtle', flexWrap: 'wrap', gap: 1 }}>
                  <Button onClick={() => setInterconsultaOpen(false)} variant="outlined">Cancelar</Button>

                  {/* Enviar por correo */}
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<EmailIcon />}
                    disabled={
                      enviandoRefEmail ||
                      generandoRefPDF ||
                      !interconsultaData.nombreDoctor ||
                      !interconsultaData.especialidad ||
                      !interconsultaData.historiaEnfermedad ||
                      !interconsultaData.email ||
                      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(interconsultaData.email).trim())
                    }
                    onClick={async () => {
                      if (!isEditMode || !consultaId) {
                        toast.info('Primero guarda la consulta para enviar la referencia');
                        return;
                      }
                      setEnviandoRefEmail(true);
                      const toastId = toast.loading('Enviando referencia por correo...');
                      try {
                        await axiosInstance.post(
                          `/consultas/${consultaId}/enviar-referencia/`,
                          {
                            email: interconsultaData.email.trim(),
                            doctor_referido: interconsultaData.nombreDoctor,
                            especialidad_referido: interconsultaData.especialidad,
                            motivo_referencia: interconsultaData.historiaEnfermedad,
                            historia_enfermedad: interconsultaData.historiaEnfermedad,
                            tratamiento_actual: interconsultaData.tratamiento,
                          }
                        );
                        toast.update(toastId, {
                          render: 'Referencia enviada por correo correctamente',
                          type: 'success',
                          isLoading: false,
                          autoClose: 3000,
                        });
                        setInterconsultaOpen(false);
                        setInterconsultaData({ nombreDoctor: '', especialidad: '', historiaEnfermedad: '', tratamiento: '', email: '' });
                      } catch (err) {
                        const msg = err?.response?.data?.error || err?.response?.data?.detail || 'No se pudo enviar la referencia por correo';
                        toast.update(toastId, {
                          render: msg,
                          type: 'error',
                          isLoading: false,
                          autoClose: 4000,
                        });
                      } finally {
                        setEnviandoRefEmail(false);
                      }
                    }}
                  >
                    {enviandoRefEmail ? 'Enviando...' : 'Enviar por correo'}
                  </Button>

                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={generandoRefPDF ? null : <CompareArrowsIcon />}
                    disabled={generandoRefPDF || !interconsultaData.nombreDoctor || !interconsultaData.especialidad || !interconsultaData.historiaEnfermedad}
                    sx={{ ml: 1 }}
                    onClick={async () => {
                      if (!isEditMode || !consultaId) {
                        toast.info('Primero guarda la consulta para generar la referencia');
                        return;
                      }
                      setGenerandoRefPDF(true);
                      try {
                        const params = new URLSearchParams({
                          doctor_referido: interconsultaData.nombreDoctor,
                          especialidad_referido: interconsultaData.especialidad,
                          motivo_referencia: interconsultaData.historiaEnfermedad,
                          historia_enfermedad: interconsultaData.historiaEnfermedad,
                          tratamiento_actual: interconsultaData.tratamiento,
                        });
                        const resp = await axiosInstance.get(
                          `/consultas/${consultaId}/referencia-pdf/?${params.toString()}`,
                          { responseType: 'blob' }
                        );
                        const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `referencia_${consultaId}.pdf`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success('Referencia médica generada correctamente');
                        setInterconsultaOpen(false);
                        setInterconsultaData({ nombreDoctor: '', especialidad: '', historiaEnfermedad: '', tratamiento: '', email: '' });
                      } catch (err) {
                        const msg = err?.response?.data?.error || 'No se pudo generar la referencia';
                        toast.error(msg);
                      } finally {
                        setGenerandoRefPDF(false);
                      }
                    }}
                  >
                    {generandoRefPDF ? 'Generando...' : 'Generar PDF'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* === Dialog de Examen Psiquiátrico === */}
              <Dialog
                open={psiquiatricoOpen}
                onClose={() => setPsiquiatricoOpen(false)}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText', py: 2 }}>
                  Examen del Estado Mental
                </DialogTitle>
                <DialogContent sx={{ p: 3, mt: 1 }}>
                  {/* Apariencia y actitud */}
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Apariencia y actitud
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.apariencia.includes(0)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          apariencia: checked ? [...prev.apariencia, 0] : prev.apariencia.filter(i => i !== 0)
                        }));
                      }} />}
                      label="Se presenta aseado, con vestimenta acorde y actitud colaboradora."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.apariencia.includes(1)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          apariencia: checked ? [...prev.apariencia, 1] : prev.apariencia.filter(i => i !== 1)
                        }));
                      }} />}
                      label="Se muestra ligeramente descuidado, aunque accesible al diálogo."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.apariencia.includes(2)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          apariencia: checked ? [...prev.apariencia, 2] : prev.apariencia.filter(i => i !== 2)
                        }));
                      }} />}
                      label="Actitud poco colaboradora, con cierta resistencia durante la entrevista."
                    />
                  </FormGroup>

                  {/* Conciencia y orientación */}
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
                    Conciencia y orientación
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.conciencia.includes(0)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          conciencia: checked ? [...prev.conciencia, 0] : prev.conciencia.filter(i => i !== 0)
                        }));
                      }} />}
                      label="Se encuentra alerta y orientado en tiempo, espacio y persona."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.conciencia.includes(1)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          conciencia: checked ? [...prev.conciencia, 1] : prev.conciencia.filter(i => i !== 1)
                        }));
                      }} />}
                      label="Alerta, con leves fallas en la orientación temporal."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.conciencia.includes(2)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          conciencia: checked ? [...prev.conciencia, 2] : prev.conciencia.filter(i => i !== 2)
                        }));
                      }} />}
                      label="Vigil pero con fluctuaciones en la atención."
                    />
                  </FormGroup>

                  {/* Lenguaje */}
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
                    Lenguaje
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.lenguaje.includes(0)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          lenguaje: checked ? [...prev.lenguaje, 0] : prev.lenguaje.filter(i => i !== 0)
                        }));
                      }} />}
                      label="Lenguaje fluido, coherente y bien articulado."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.lenguaje.includes(1)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          lenguaje: checked ? [...prev.lenguaje, 1] : prev.lenguaje.filter(i => i !== 1)
                        }));
                      }} />}
                      label="Lenguaje escaso, con latencias en la respuesta."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.lenguaje.includes(2)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          lenguaje: checked ? [...prev.lenguaje, 2] : prev.lenguaje.filter(i => i !== 2)
                        }));
                      }} />}
                      label="Discurso acelerado, difícil de interrumpir."
                    />
                  </FormGroup>

                  {/* Ánimo y afecto */}
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
                    Ánimo y afecto
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.animo.includes(0)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          animo: checked ? [...prev.animo, 0] : prev.animo.filter(i => i !== 0)
                        }));
                      }} />}
                      label="Refiere ánimo bajo; afecto congruente, con reactividad conservada."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.animo.includes(1)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          animo: checked ? [...prev.animo, 1] : prev.animo.filter(i => i !== 1)
                        }));
                      }} />}
                      label="Ánimo ansioso, con afecto tenso pero modulable."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.animo.includes(2)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          animo: checked ? [...prev.animo, 2] : prev.animo.filter(i => i !== 2)
                        }));
                      }} />}
                      label="Ánimo eutímico, con afecto estable."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.animo.includes(3)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          animo: checked ? [...prev.animo, 3] : prev.animo.filter(i => i !== 3)
                        }));
                      }} />}
                      label="Afecto plano, con escasa expresividad emocional."
                    />
                  </FormGroup>

                  {/* Pensamiento */}
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
                    Pensamiento (curso y contenido)
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.pensamiento.includes(0)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          pensamiento: checked ? [...prev.pensamiento, 0] : prev.pensamiento.filter(i => i !== 0)
                        }));
                      }} />}
                      label="El pensamiento es organizado, sin evidencia de ideas delirantes ni ideación suicida."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.pensamiento.includes(1)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          pensamiento: checked ? [...prev.pensamiento, 1] : prev.pensamiento.filter(i => i !== 1)
                        }));
                      }} />}
                      label="Se observa rumiación persistente, sin contenido delirante."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.pensamiento.includes(2)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          pensamiento: checked ? [...prev.pensamiento, 2] : prev.pensamiento.filter(i => i !== 2)
                        }));
                      }} />}
                      label="Contenido del pensamiento con ideas de minusvalía y culpa."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.pensamiento.includes(3)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          pensamiento: checked ? [...prev.pensamiento, 3] : prev.pensamiento.filter(i => i !== 3)
                        }));
                      }} />}
                      label={<span style={{ color: '#d32f2f' }}>⚠️ Expresa ideación suicida, sin plan estructurado.</span>}
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.pensamiento.includes(4)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          pensamiento: checked ? [...prev.pensamiento, 4] : prev.pensamiento.filter(i => i !== 4)
                        }));
                      }} />}
                      label={<span style={{ color: '#d32f2f' }}>⚠️ Se identifican ideas delirantes de tipo persecutorio.</span>}
                    />
                  </FormGroup>

                  {/* Percepción */}
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
                    Percepción
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.percepcion.includes(0)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          percepcion: checked ? [...prev.percepcion, 0] : prev.percepcion.filter(i => i !== 0)
                        }));
                      }} />}
                      label="No se evidencian alteraciones perceptivas."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.percepcion.includes(1)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          percepcion: checked ? [...prev.percepcion, 1] : prev.percepcion.filter(i => i !== 1)
                        }));
                      }} />}
                      label={<span style={{ color: '#d32f2f' }}>⚠️ Refiere fenómenos perceptivos auditivos compatibles con alucinaciones.</span>}
                    />
                  </FormGroup>

                  {/* Cognición */}
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
                    Cognición
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.cognicion.includes(0)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          cognicion: checked ? [...prev.cognicion, 0] : prev.cognicion.filter(i => i !== 0)
                        }));
                      }} />}
                      label="Funciones cognitivas globalmente conservadas."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.cognicion.includes(1)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          cognicion: checked ? [...prev.cognicion, 1] : prev.cognicion.filter(i => i !== 1)
                        }));
                      }} />}
                      label="Atención disminuida durante la entrevista."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.cognicion.includes(2)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          cognicion: checked ? [...prev.cognicion, 2] : prev.cognicion.filter(i => i !== 2)
                        }));
                      }} />}
                      label="Dificultades leves en memoria reciente."
                    />
                  </FormGroup>

                  {/* Insight y juicio */}
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
                    Insight y juicio
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.insight.includes(0)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          insight: checked ? [...prev.insight, 0] : prev.insight.filter(i => i !== 0)
                        }));
                      }} />}
                      label="Reconoce parcialmente su estado y la necesidad de tratamiento."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.insight.includes(1)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          insight: checked ? [...prev.insight, 1] : prev.insight.filter(i => i !== 1)
                        }));
                      }} />}
                      label="Escaso insight respecto a su condición."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.insight.includes(2)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          insight: checked ? [...prev.insight, 2] : prev.insight.filter(i => i !== 2)
                        }));
                      }} />}
                      label="Juicio conservado para la vida cotidiana."
                    />
                    <FormControlLabel
                      control={<Checkbox checked={psiquiatricoSelections.insight.includes(3)} onChange={(e) => {
                        const checked = e.target.checked;
                        setPsiquiatricoSelections(prev => ({
                          ...prev,
                          insight: checked ? [...prev.insight, 3] : prev.insight.filter(i => i !== 3)
                        }));
                      }} />}
                      label="Juicio comprometido en la toma de decisiones."
                    />
                  </FormGroup>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.subtle' }}>
                  <Button onClick={() => setPsiquiatricoOpen(false)}>Cancelar</Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      // Generar HTML desde las selecciones
                      const opciones = {
                        apariencia: [
                          'Se presenta aseado, con vestimenta acorde y actitud colaboradora.',
                          'Se muestra ligeramente descuidado, aunque accesible al diálogo.',
                          'Actitud poco colaboradora, con cierta resistencia durante la entrevista.'
                        ],
                        conciencia: [
                          'Se encuentra alerta y orientado en tiempo, espacio y persona.',
                          'Alerta, con leves fallas en la orientación temporal.',
                          'Vigil pero con fluctuaciones en la atención.'
                        ],
                        lenguaje: [
                          'Lenguaje fluido, coherente y bien articulado.',
                          'Lenguaje escaso, con latencias en la respuesta.',
                          'Discurso acelerado, difícil de interrumpir.'
                        ],
                        anim: [
                          'Refiere ánimo bajo; afecto congruente, con reactividad conservada.',
                          'Ánimo ansioso, con afecto tenso pero modulable.',
                          'Ánimo eutímico, con afecto estable.',
                          'Afecto plano, con escasa expresividad emocional.'
                        ],
                        pensamiento: [
                          'El pensamiento es organizado, sin evidencia de ideas delirantes ni ideación suicida.',
                          'Se observa rumiación persistente, sin contenido delirante.',
                          'Contenido del pensamiento con ideas de minusvalía y culpa.',
                          'Expresa ideación suicida, sin plan estructurado. ⚠️',
                          'Se identifican ideas delirantes de tipo persecutorio. ⚠️'
                        ],
                        percepcion: [
                          'No se evidencian alteraciones perceptivas.',
                          'Refiere fenómenos perceptivos auditivos compatibles con alucinaciones. ⚠️'
                        ],
                        cognicion: [
                          'Funciones cognitivas globalmente conservadas.',
                          'Atención disminuida durante la entrevista.',
                          'Dificultades leves en memoria reciente.'
                        ],
                        insight: [
                          'Reconoce parcialmente su estado y la necesidad de tratamiento.',
                          'Escaso insight respecto a su condición.',
                          'Juicio conservado para la vida cotidiana.',
                          'Juicio comprometido en la toma de decisiones.'
                        ]
                      };

                      const generarSeccion = (titulo, categoria, opcionesArr) => {
                        const seleccionados = psiquiatricoSelections[categoria];
                        if (seleccionados.length === 0) return '';
                        const lineas = seleccionados.map(idx => `[x] ${opcionesArr[idx]}`).join('<br>\n');
                        return `<p><strong>${titulo}:</strong><br>\n${lineas}<br>\n</p>\n`;
                      };

                      let html = '<p><strong>Examen del Estado Mental</strong></p>\n';
                      html += generarSeccion('Apariencia y actitud', 'apariencia', opciones.apariencia);
                      html += generarSeccion('Conciencia y orientación', 'conciencia', opciones.conciencia);
                      html += generarSeccion('Lenguaje', 'lenguaje', opciones.lenguaje);
                      html += generarSeccion('Ánimo y afecto', 'animo', opciones.anim);
                      html += generarSeccion('Pensamiento (curso y contenido)', 'pensamiento', opciones.pensamiento);
                      html += generarSeccion('Percepción', 'percepcion', opciones.percepcion);
                      html += generarSeccion('Cognición', 'cognicion', opciones.cognicion);
                      html += generarSeccion('Insight y juicio', 'insight', opciones.insight);

                      // Actualizar el campo examen_fisico
                      setFormData(prev => ({ ...prev, examen_fisico: html }));
                      if (editorRef.current) {
                        editorRef.current.value = html;
                      }
                      
                      // Cerrar diálogo
                      setPsiquiatricoOpen(false);
                      toast.success('Examen psiquiátrico generado');
                    }}
                  >
                    Generar Examen
                  </Button>
                </DialogActions>
              </Dialog>

            </Box>
          );
        };

export default AddConsultaPage;